class FinancialSummaryMath {
  static BREAKEVEN_MAX_PROBES = 300;
  static BREAKEVEN_MAX_TICKETS = 100000;
  static BREAKEVEN_SWEEP_SAMPLES = 24;
  static BREAKEVEN_MAX_PLAN_MULTIPLE = 8;
  static BREAKEVEN_MIN_SEARCH_CEILING = 5000;

  static GROSS_TICKET_SALES_BASIS = 'gross_ticket_sales';

  static NET_GROSS_BASIS_CALC_TYPES = ['percentage_of_net_gross', 'tax_inclusive_percentage_of_net_gross'];
  static TAX_INCLUSIVE_CALC_TYPES = ['tax_inclusive_percentage', 'tax_inclusive_percentage_of_net_gross'];
  static PERCENT_CALC_TYPES = ['percentage_of_gross', 'tax_inclusive_percentage', 'percentage_of_net_gross', 'tax_inclusive_percentage_of_net_gross'];
  static MULTIPLY_CALC_TYPES = ['fixed', 'per_tickets_sold'];
  static FINAL_EXPENSE_CALC_TYPES = ['percentage_of_net', 'percentage_of_expenses'];

  static operatorForCalcType(calcType) {
    return FinancialSummaryMath.MULTIPLY_CALC_TYPES.includes(calcType) ? '*' : 'of';
  }

  static columnOperator(item, columnCalcType) {
    const multipliesInOffer = FinancialSummaryMath.operatorForCalcType(item.calc_type || 'fixed') === '*';
    const percentInColumn = FinancialSummaryMath.operatorForCalcType(columnCalcType) === 'of';
    if (item.operator === '*' && multipliesInOffer && percentInColumn) return 'of';
    return item.operator;
  }

  static isFinalExpenseItem(item) {
    const finalTypes = FinancialSummaryMath.FINAL_EXPENSE_CALC_TYPES;
    if (finalTypes.includes(item.calc_type)) return true;
    if (item.show_in_offer ?? true) return false;
    return finalTypes.includes(item.calc_type_forecast) || finalTypes.includes(item.calc_type_actual);
  }

  static hasAmount(value) {
    return value != null && value !== '' && parseFloat(value) !== 0;
  }

  static hasQuantity(value) {
    return value != null && value !== '';
  }

  static forecastLeftHand(item) {
    return FinancialSummaryMath.hasAmount(item.left_hand_forecast) ? item.left_hand_forecast : item.left_hand;
  }

  static forecastRightHand(item) {
    return FinancialSummaryMath.hasQuantity(item.right_hand_forecast) ? item.right_hand_forecast : item.right_hand;
  }

  static forecastCalcType(item) {
    return item.calc_type_forecast || item.calc_type || 'fixed';
  }

  static columnCalcTypes(item) {
    const forecast = FinancialSummaryMath.forecastCalcType(item);
    return { offer: item.calc_type || 'fixed', forecast, actual: item.calc_type_actual || forecast };
  }

  static isNetGrossInAnyColumn(item) {
    return Object.values(FinancialSummaryMath.columnCalcTypes(item))
      .some(calcType => FinancialSummaryMath.NET_GROSS_BASIS_CALC_TYPES.includes(calcType));
  }

  static forecastAsOfferInputs(item) {
    const calcTypeForecast = FinancialSummaryMath.forecastCalcType(item);
    return {
      left_hand: FinancialSummaryMath.forecastLeftHand(item),
      right_hand: FinancialSummaryMath.forecastRightHand(item),
      calc_type: calcTypeForecast,
      operator: FinancialSummaryMath.columnOperator(item, calcTypeForecast),
    };
  }

  constructor(offer, budget, isSettling) {
    this.offer = offer || {};
    this.budget = budget || {};
    this.isSettling = isSettling || false;

    this.ticketLineItemsKey = this.budget.ticket_line_items ? 'ticket_line_items' : 'v3_budget_line_items_tickets';
    this.ticketLineItems = this.budget[this.ticketLineItemsKey] || [];
    this.additionLineItems = this.budget.addition_line_items || this.budget.v3_budget_line_items_additions || [];
    this.incomeLineItems = this.budget.income_line_items || this.budget.v3_budget_line_items_income || [];
    this.beforeAdjustedDeductionsLineItems = this.budget.before_adjusted_deductions_line_items || this.budget.v3_budget_line_items_before_adjusted_deductions || [];
    this.expenseLineItems = this.budget.expense_line_items || this.budget.v3_budget_line_items_expenses || [];
    this.deductionLineItems = this.budget.deduction_line_items || this.budget.v3_budget_line_items_deductions || [];
    this.dealBrackets = this.budget.v3_deal_brackets || this.budget.deal_brackets || [];
    this.budgetBonuses = this.scopedToOffer(this.budget.v3_budget_bonuses || this.budget.budget_bonuses);
    this.deposits = this.scopedToOffer(this.budget.v3_budget_deposits || this.budget.deposits);
    this.poolMembers = this.offer.pool_members || [];
  }

  scopedToOffer(rows) {
    return (rows || []).filter((row) => row.v3_offer_id == null || row.v3_offer_id === this.offer.id);
  }

  calculate() {
    this.ticketsSection = this.calculateSection(this.ticketLineItems);
    this.revenueSection = this.calculateSection(this.incomeLineItems);

    // O-4509: when the offer applies tax after the facility fee, the offer's
    // existing tax rows (per-ticket ManualTax injected at settlement, or a line
    // item marked kind 'admission_tax') are pulled OUT of the before-adjusted
    // deductions so they don't shrink the deal, then re-surfaced as a
    // pass-through line re-based to the net gate (see admissionTax). Off ⇒ they
    // stay in before-adjusted deductions and behave byte-for-byte as today.
    const passThroughTax = !!this.offer.apply_tax_after_facility_fee;
    this.admissionTaxRows = passThroughTax
      ? this.selectAdmissionTaxRows(this.beforeAdjustedDeductionsLineItems)
      : [];
    // Tax already inside the gate (settlement grouped the ticket price with the
    // tax in it) must KEEP deducting — that subtraction is what brings the gate
    // down to face. Only tax sitting outside the gate gets lifted out. Both
    // still surface as the pass-through line. (O-4509)
    const liftedRows = new Set(this.admissionTaxRows.filter((i) => !this.isTaxInsideGate(i)));
    this.liftedTaxRows = liftedRows;
    const beforeAdjustedItems = passThroughTax
      ? this.beforeAdjustedDeductionsLineItems.filter((i) => !liftedRows.has(i))
      : this.beforeAdjustedDeductionsLineItems;
    this.beforeAdjustedDeductionsSection = this.calculateSection(beforeAdjustedItems);
    this.admissionTaxSection = this.calculateSection(this.admissionTaxRows);

    this.showSummary = this.calculateNetBoxOfficeReceipts();

    this.expensesSection = this.calculateSection(this.buildExpenseItems());
    this.showSummary = this.calculateShowNetAfterExpenses();

    this.finalExpensesSection = this.calculateSection(this.buildFinalExpenseItems());
    this.showSummary = this.calculateShowNetAfterFinalExpenses();

    this.showSummary = this.calculateShowSummary();

    // Admission tax (O-4509) is a pass-through: the offer's existing tax,
    // re-based to the net gate (gross - facility maintenance fee) and reported
    // on top to form the gross gate, but never subtracted from NBOR, the deal,
    // or the venue net. Zero unless apply_tax_after_facility_fee is on.
    this.showSummary.admissionTax = this.admissionTax('offer');
    this.showSummary.admissionTaxForecast = this.admissionTax('forecast');
    this.showSummary.admissionTaxActual = this.admissionTax('actual');
    this.rebaseLiftedTaxRowsForDisplay();

    this.additionsSection = this.calculateSection(this.buildAdditionLineItems());
    this.deductionsSection = this.calculateSection(this.deductionLineItems);

    this.depositsSection = this.calculateDepositsSection();
    this.artistEarnings = this.calculateArtistEarnings('offer');
    this.artistEarningsForecast = this.calculateArtistEarnings('forecast');
    this.artistEarningsActual = this.calculateArtistEarnings('actual');

    this.poolMembersOffer = this.calculatePoolMembers('offer');
    this.poolMembersForecast = this.calculatePoolMembers('forecast');
    this.poolMembersActual = this.calculatePoolMembers('actual');

    const showNetForVenue = this.showSummary.showNetAfterFinalExpenses != null ? this.showSummary.showNetAfterFinalExpenses : this.showSummary.showNetAfterExpenses || 0;
    const showNetForVenueForecast = this.showSummary.showNetAfterFinalExpensesForecast != null ? this.showSummary.showNetAfterFinalExpensesForecast : this.showSummary.showNetAfterExpensesForecast || 0;
    const showNetForVenueActual = this.showSummary.showNetAfterFinalExpensesActual != null ? this.showSummary.showNetAfterFinalExpensesActual : this.showSummary.showNetAfterExpensesActual || 0;
    Object.assign(this.showSummary, this.calculateAncillaryAmounts());
    this.showSummary.venueNetPotential = new Decimal(showNetForVenue)
      .minus(this.artistEarnings.artistTotalDue)
      .plus(this.showSummary.ancillaryNet).toNumber();
    this.showSummary.venueNetPotentialForecast = new Decimal(showNetForVenueForecast)
      .minus(this.artistEarningsForecast.artistTotalDue)
      .plus(this.showSummary.ancillaryNetForecast).toNumber();
    this.showSummary.venueNetPotentialActual = new Decimal(showNetForVenueActual)
      .minus(this.artistEarningsActual.artistTotalDue)
      .plus(this.showSummary.ancillaryNetActual).toNumber();
    const venueNetExcludingAncillary = new Decimal(showNetForVenue)
      .minus(this.artistEarnings.artistTotalDue)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    this.showSummary.venueNetExcludingAncillary = venueNetExcludingAncillary.toNumber() || 0;
    this.showSummary.expensesAndArtistPayout = new Decimal(this.showSummary.netBoxOfficeReceipts)
      .minus(venueNetExcludingAncillary)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber() || 0;

    if (this.solveBreakeven) {
      try {
        this.attachTicketsToBreakeven();
      } catch (error) {
        this.breakevenError = String((error && error.message) || error);
      }
    }

    return JSON.stringify({
      breakevenError: this.breakevenError || null,
      ticketsSection: this.ticketsSection,
      revenueSection: this.revenueSection,
      beforeAdjustedDeductionsSection: this.beforeAdjustedDeductionsSection,
      // O-4509: a tax row being passed through is totalled here instead of in
      // the deductions section, so the editor needs it to render that row's
      // real figures rather than falling back to a $0.
      admissionTaxSection: this.admissionTaxSection,
      expensesSection: this.expensesSection,
      finalExpensesSection: this.finalExpensesSection,
      additionsSection: this.additionsSection,
      deductionsSection: this.deductionsSection,
      depositsSection: this.depositsSection,
      showSummary: this.showSummary,
      artistEarnings: this.artistEarnings,
      artistEarningsForecast: this.artistEarningsForecast,
      artistEarningsActual: this.artistEarningsActual,
      poolMembers: this.poolMembersOffer,
      poolMembersForecast: this.poolMembersForecast,
      poolMembersActual: this.poolMembersActual,
    });
  }

  calculateSection(items) {
    const needsTwoPass = !this.showSummary && items.some(i => FinancialSummaryMath.isNetGrossInAnyColumn(i));

    let pass1Items;
    if (needsTwoPass) {
      pass1Items = items.map(item => {
        if (FinancialSummaryMath.NET_GROSS_BASIS_CALC_TYPES.includes(item.calc_type)) return item;
        const calculated = this.calculateLineItem(item, items, []);
        return calculated;
      });
    }

    const calculatedItems = [];
    const sourceItems = pass1Items || items;

    for (let i = 0; i < sourceItems.length; i++) {
      const item = sourceItems[i];
      let calculated;
      if (needsTwoPass && FinancialSummaryMath.isNetGrossInAnyColumn(items[i])) {
        calculated = this.calculateLineItem(items[i], items, pass1Items);
      } else if (needsTwoPass) {
        calculated = item;
      } else {
        calculated = this.calculateLineItem(item, items, calculatedItems);
      }
      calculatedItems.push(calculated);
    }

    return this.sumSectionItems(calculatedItems);
  }

  sumSectionItems(calculatedItems) {
    let leftTotal = new Decimal(0);
    let rightTotal = new Decimal(0);
    let total = new Decimal(0);
    let leftForecastTotal = new Decimal(0);
    let rightForecastTotal = new Decimal(0);
    let forecastTotal = new Decimal(0);
    let leftActualTotal = new Decimal(0);
    let rightActualTotal = new Decimal(0);
    let actualTotal = new Decimal(0);

    let externalTotal = new Decimal(0);
    let externalForecastTotal = new Decimal(0);
    let externalActualTotal = new Decimal(0);
    let externalRightTotal = new Decimal(0);
    let externalRightForecastTotal = new Decimal(0);
    let externalRightActualTotal = new Decimal(0);
    let internalTotal = new Decimal(0);
    let internalForecastTotal = new Decimal(0);
    let internalActualTotal = new Decimal(0);
    let internalRightTotal = new Decimal(0);
    let internalRightForecastTotal = new Decimal(0);
    let internalRightActualTotal = new Decimal(0);

    for (const calculated of calculatedItems) {
      leftTotal = leftTotal.plus(calculated.calculatedLeftHand);
      rightTotal = rightTotal.plus(calculated.calculatedRightHand);
      total = total.plus(calculated.calculatedTotal);
      leftForecastTotal = leftForecastTotal.plus(calculated.calculatedLeftHandForecast);
      rightForecastTotal = rightForecastTotal.plus(calculated.calculatedRightHandForecast);
      forecastTotal = forecastTotal.plus(calculated.calculatedForecastTotal);
      leftActualTotal = leftActualTotal.plus(calculated.calculatedLeftHandActual || 0);
      rightActualTotal = rightActualTotal.plus(calculated.calculatedRightHandActual || 0);
      actualTotal = actualTotal.plus(calculated.calculatedActualTotal || 0);

      if (calculated.show_in_offer !== false) {
        externalTotal = externalTotal.plus(calculated.calculatedTotal);
        externalForecastTotal = externalForecastTotal.plus(calculated.calculatedForecastTotal);
        externalActualTotal = externalActualTotal.plus(calculated.calculatedActualTotal || 0);
        externalRightTotal = externalRightTotal.plus(calculated.calculatedRightHand);
        externalRightForecastTotal = externalRightForecastTotal.plus(calculated.calculatedRightHandForecast);
        externalRightActualTotal = externalRightActualTotal.plus(calculated.calculatedRightHandActual || 0);
      } else {
        internalTotal = internalTotal.plus(calculated.calculatedTotal);
        internalForecastTotal = internalForecastTotal.plus(calculated.calculatedForecastTotal);
        internalActualTotal = internalActualTotal.plus(calculated.calculatedActualTotal || 0);
        internalRightTotal = internalRightTotal.plus(calculated.calculatedRightHand);
        internalRightForecastTotal = internalRightForecastTotal.plus(calculated.calculatedRightHandForecast);
        internalRightActualTotal = internalRightActualTotal.plus(calculated.calculatedRightHandActual || 0);
      }
    }

    return {
      items: calculatedItems,
      leftTotal: leftTotal.toNumber(),
      rightTotal: rightTotal.toNumber(),
      total: total.toNumber(),
      leftForecastTotal: leftForecastTotal.toNumber(),
      rightForecastTotal: rightForecastTotal.toNumber(),
      forecastTotal: forecastTotal.toNumber(),
      leftActualTotal: leftActualTotal.toNumber(),
      rightActualTotal: rightActualTotal.toNumber(),
      actualTotal: actualTotal.toNumber(),
      externalTotal: externalTotal.toNumber(),
      externalForecastTotal: externalForecastTotal.toNumber(),
      externalActualTotal: externalActualTotal.toNumber(),
      externalRightTotal: externalRightTotal.toNumber(),
      externalRightForecastTotal: externalRightForecastTotal.toNumber(),
      externalRightActualTotal: externalRightActualTotal.toNumber(),
      internalTotal: internalTotal.toNumber(),
      internalForecastTotal: internalForecastTotal.toNumber(),
      internalActualTotal: internalActualTotal.toNumber(),
      internalRightTotal: internalRightTotal.toNumber(),
      internalRightForecastTotal: internalRightForecastTotal.toNumber(),
      internalRightActualTotal: internalRightActualTotal.toNumber(),
    };
  }

  calculateLineItem(item, sectionItems, calculatedItems) {
    const leftHand = this.parseMaskedInput(item.left_hand);
    const rightHand = this.parseMaskedInput(item.right_hand);
    const leftHandForecast = this.parseMaskedInput(FinancialSummaryMath.forecastLeftHand(item));
    const rightHandForecast = this.parseMaskedInput(FinancialSummaryMath.forecastRightHand(item));
    const hasActualLeft = FinancialSummaryMath.hasAmount(item.left_hand_actual);
    const hasActualRight = FinancialSummaryMath.hasQuantity(item.right_hand_actual);
    const leftHandActual = this.parseMaskedInput(hasActualLeft ? item.left_hand_actual : FinancialSummaryMath.forecastLeftHand(item));
    const rightHandActual = this.parseMaskedInput(hasActualRight ? item.right_hand_actual : FinancialSummaryMath.forecastRightHand(item));

    const { offer: calcType, forecast: calcTypeForecast, actual: calcTypeActual } = FinancialSummaryMath.columnCalcTypes(item);

    const offerResult = this.applyCalcType(leftHand, rightHand, item.operator, calcType, 'offer', item, calculatedItems);
    const forecastResult = this.applyCalcType(leftHandForecast, rightHandForecast, FinancialSummaryMath.columnOperator(item, calcTypeForecast), calcTypeForecast, 'forecast', item, calculatedItems);
    const actualResult = this.applyCalcType(leftHandActual, rightHandActual, FinancialSummaryMath.columnOperator(item, calcTypeActual), calcTypeActual, 'actual', item, calculatedItems);

    const applyMinMax = (val) => {
      let result = val;
      if (item.min_value != null && result.lessThan(new Decimal(item.min_value))) {
        result = new Decimal(item.min_value);
      }
      if (item.max_value != null && result.greaterThan(new Decimal(item.max_value))) {
        result = new Decimal(item.max_value);
      }
      return result;
    };

    return {
      ...item,
      calculatedLeftHand: leftHand.toNumber(),
      calculatedRightHand: this.resolveCalcTypeRightHand(rightHand, calcType, 'offer', item, calculatedItems).toNumber(),
      calculatedTotal: applyMinMax(offerResult).toNumber(),
      calculatedLeftHandForecast: leftHandForecast.toNumber(),
      calculatedRightHandForecast: this.resolveCalcTypeRightHand(rightHandForecast, calcTypeForecast, 'forecast', item, calculatedItems).toNumber(),
      calculatedForecastTotal: applyMinMax(forecastResult).toNumber(),
      calculatedLeftHandActual: leftHandActual.toNumber(),
      calculatedRightHandActual: this.resolveCalcTypeRightHand(rightHandActual, calcTypeActual, 'actual', item, calculatedItems).toNumber(),
      calculatedActualTotal: applyMinMax(actualResult).toNumber(),
    };
  }

  resolveCalcTypeRightHand(rightHand, calcType, mode, item, calculatedItems) {
    switch (calcType) {
      case 'per_tickets_sold':
        return new Decimal(this.numberOfPaidTickets(mode));
      case 'percentage_of_gross':
      case 'tax_inclusive_percentage':
        if (mode === 'forecast') {
          return new Decimal((this.ticketsSection.externalForecastTotal || 0) + (this.revenueSection.externalForecastTotal || 0));
        } else if (mode === 'actual') {
          return new Decimal((this.ticketsSection.externalActualTotal || 0) + (this.revenueSection.externalActualTotal || 0));
        }
        return new Decimal(this.grossPotential());
      case 'percentage_of_net_gross':
      case 'tax_inclusive_percentage_of_net_gross':
        if (this.showSummary && this.showSummary.netBoxOfficeReceipts !== undefined) {
          if (mode === 'forecast') {
            return new Decimal(this.showSummary.netBoxOfficeReceiptsForecast || 0);
          } else if (mode === 'actual') {
            return new Decimal(this.showSummary.netBoxOfficeReceiptsActual || 0);
          }
          return new Decimal(this.showSummary.netBoxOfficeReceipts || 0);
        } else if (calculatedItems && calculatedItems.length > 0) {
          const grossKey = mode === 'forecast' ? 'forecastTotal' : mode === 'actual' ? 'actualTotal' : 'total';
          const calcKey = mode === 'forecast' ? 'calculatedForecastTotal' : mode === 'actual' ? 'calculatedActualTotal' : 'calculatedTotal';
          const gross = (this.ticketsSection[grossKey] || 0) + (this.revenueSection['external' + grossKey.charAt(0).toUpperCase() + grossKey.slice(1)] || 0);
          const selfId = item && (item.uuid || item.id);
          const otherDeductions = calculatedItems
            .filter(i => {
              if (selfId && (i.uuid || i.id) === selfId) return false;
              const columnCalcType = FinancialSummaryMath.columnCalcTypes(i)[mode];
              return !FinancialSummaryMath.NET_GROSS_BASIS_CALC_TYPES.includes(columnCalcType) && i[calcKey] !== undefined;
            })
            .reduce((sum, i) => sum + i[calcKey], 0);
          return new Decimal(gross - otherDeductions);
        } else {
          if (mode === 'forecast') {
            return new Decimal((this.ticketsSection.forecastTotal || 0) + (this.revenueSection.externalForecastTotal || 0));
          } else if (mode === 'actual') {
            return new Decimal((this.ticketsSection.actualTotal || 0) + (this.revenueSection.externalActualTotal || 0));
          }
          return new Decimal(this.grossPotential());
        }
      case 'percentage_of_net':
        if (!this.showSummary) return new Decimal(0);
        if (mode === 'forecast') {
          return new Decimal(this.showSummary.showNetAfterExpensesForecast || 0);
        } else if (mode === 'actual') {
          return new Decimal(this.showSummary.showNetAfterExpensesActual || 0);
        }
        return new Decimal(this.showSummary.showNetAfterExpenses || 0);
      case 'percentage_of_expenses':
        if (!this.expensesSection) return new Decimal(0);
        if (mode === 'forecast') {
          return new Decimal(this.expensesSection.externalForecastTotal || 0);
        } else if (mode === 'actual') {
          return new Decimal(this.expensesSection.externalActualTotal || 0);
        }
        return new Decimal(this.expensesSection.externalTotal || 0);
      default:
        return rightHand;
    }
  }

  applyCalcType(leftHand, rightHand, operator, calcType, mode, item, calculatedItems) {
    const resolvedRight = this.resolveCalcTypeRightHand(rightHand, calcType, mode, item, calculatedItems);

    if (FinancialSummaryMath.TAX_INCLUSIVE_CALC_TYPES.includes(calcType)) {
      const divisor = new Decimal(1).plus(leftHand.dividedBy(100));
      if (divisor.isZero()) return new Decimal(0);
      return resolvedRight.minus(resolvedRight.dividedBy(divisor))
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    }

    switch (operator) {
      case '*':
        return leftHand.times(resolvedRight);
      case '+':
        return leftHand.plus(resolvedRight);
      case '-':
        return leftHand.minus(resolvedRight);
      case '÷':
      case '/':
        return resolvedRight.isZero() ? new Decimal(0) : leftHand.dividedBy(resolvedRight);
      case 'of':
        return leftHand.times(resolvedRight).dividedBy(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
      default:
        return leftHand.times(resolvedRight);
    }
  }

  calculateNetBoxOfficeReceipts() {
    // Ancillary revenue (show_in_offer=false, e.g. bar/concessions) is venue-only and
    // must stay out of NBOR in every mode, otherwise it feeds the artist's revenue base.
    const netBoxOfficeReceipts = new Decimal(this.ticketsSection.externalTotal || 0)
      .plus(this.revenueSection.externalTotal || 0)
      .minus(this.beforeAdjustedDeductionsSection.externalTotal || 0);

    const netBoxOfficeReceiptsForecast = new Decimal(this.ticketsSection.externalForecastTotal || 0)
      .plus(this.revenueSection.externalForecastTotal || 0)
      .minus(this.beforeAdjustedDeductionsSection.externalForecastTotal || 0);

    const netBoxOfficeReceiptsActual = new Decimal(this.ticketsSection.externalActualTotal || 0)
      .plus(this.revenueSection.externalActualTotal || 0)
      .minus(this.beforeAdjustedDeductionsSection.externalActualTotal || 0);

    return {
      netBoxOfficeReceipts: netBoxOfficeReceipts.toNumber(),
      netBoxOfficeReceiptsForecast: netBoxOfficeReceiptsForecast.toNumber(),
      netBoxOfficeReceiptsActual: netBoxOfficeReceiptsActual.toNumber(),
    };
  }

  calculateAncillaryAmounts() {
    const ancillaryIncome = new Decimal(this.revenueSection.internalTotal || 0)
      .plus(this.ticketsSection.internalTotal || 0);
    const ancillaryIncomeForecast = new Decimal(this.revenueSection.internalForecastTotal || 0)
      .plus(this.ticketsSection.internalForecastTotal || 0);
    const ancillaryIncomeActual = new Decimal(this.revenueSection.internalActualTotal || 0)
      .plus(this.ticketsSection.internalActualTotal || 0);

    const ancillaryExpenses = new Decimal(this.expensesSection.internalTotal || 0)
      .plus(this.finalExpensesSection.internalTotal || 0);
    const ancillaryExpensesForecast = new Decimal(this.expensesSection.internalForecastTotal || 0)
      .plus(this.finalExpensesSection.internalForecastTotal || 0);
    const ancillaryExpensesActual = new Decimal(this.expensesSection.internalActualTotal || 0)
      .plus(this.finalExpensesSection.internalActualTotal || 0);

    return {
      ancillaryIncome: ancillaryIncome.toNumber(),
      ancillaryIncomeForecast: ancillaryIncomeForecast.toNumber(),
      ancillaryIncomeActual: ancillaryIncomeActual.toNumber(),
      ancillaryExpenses: ancillaryExpenses.toNumber(),
      ancillaryExpensesForecast: ancillaryExpensesForecast.toNumber(),
      ancillaryExpensesActual: ancillaryExpensesActual.toNumber(),
      ancillaryNet: ancillaryIncome.minus(ancillaryExpenses).toNumber(),
      ancillaryNetForecast: ancillaryIncomeForecast.minus(ancillaryExpensesForecast).toNumber(),
      ancillaryNetActual: ancillaryIncomeActual.minus(ancillaryExpensesActual).toNumber(),
    };
  }

  calculateShowNetAfterExpenses() {
    const showNetAfterExpenses = new Decimal(this.showSummary.netBoxOfficeReceipts || 0)
      .minus(this.expensesSection.externalTotal || 0);

    const showNetAfterExpensesForecast = new Decimal(this.showSummary.netBoxOfficeReceiptsForecast || 0)
      .minus(this.expensesSection.externalForecastTotal || 0);

    const showNetAfterExpensesActual = new Decimal(this.showSummary.netBoxOfficeReceiptsActual || 0)
      .minus(this.expensesSection.externalActualTotal || 0);

    return {
      ...this.showSummary,
      showNetAfterExpenses: showNetAfterExpenses.toNumber(),
      showNetAfterExpensesForecast: showNetAfterExpensesForecast.toNumber(),
      showNetAfterExpensesActual: showNetAfterExpensesActual.toNumber(),
    };
  }

  preSplitDeductionsAgainstBase(mode, revenueBase, unclampedToBeShared, breakevenSet) {
    const gross = new Decimal(this.grossPotentialForMode(mode));
    const base = revenueBase !== null ? revenueBase : (breakevenSet ? gross : unclampedToBeShared);
    const threshold = this.offer.breakeven_threshold != null
      ? this.parseMaskedInput(this.offer.breakeven_threshold)
      : new Decimal(0);
    return gross.minus(base).plus(threshold).toNumber();
  }

  applyBreakevenGate(baseAmount) {
    if (this.offer.breakeven_threshold == null) return baseAmount;
    const threshold = this.parseMaskedInput(this.offer.breakeven_threshold);
    return Decimal.max(new Decimal(0), baseAmount.minus(threshold));
  }

  calculateShowSummary() {
    const showNetAfterExpenses = new Decimal(this.showSummary.showNetAfterFinalExpenses != null ? this.showSummary.showNetAfterFinalExpenses : this.showSummary.showNetAfterExpenses || 0);
    const showNetAfterExpensesForecast = new Decimal(this.showSummary.showNetAfterFinalExpensesForecast != null ? this.showSummary.showNetAfterFinalExpensesForecast : this.showSummary.showNetAfterExpensesForecast || 0);
    const showNetAfterExpensesActual = new Decimal(this.showSummary.showNetAfterFinalExpensesActual != null ? this.showSummary.showNetAfterFinalExpensesActual : this.showSummary.showNetAfterExpensesActual || 0);

    const dollarAmount = this.parseMaskedInput(this.offer.dollar_amount);
    const upsidePercentage = this.finalUpsidePercentage(this.numberOfPaidTickets());
    const upsidePercentageForecast = this.finalUpsidePercentage(this.numberOfPaidTickets('forecast'));
    const upsidePercentageActual = this.finalUpsidePercentage(this.numberOfPaidTickets('actual'));
    const dollarOrPercentageOperator = this.offer.dollar_or_percentage_operator || 'DOLLAR';
    const fromDollarOne = this.offer.from_dollar_one;

    let artistGuarantee = dollarAmount;
    let bracketResult = null;
    let bracketResultForecast = null;
    let bracketResultActual = null;

    if (!fromDollarOne && this.dealBrackets.length > 0) {
      bracketResult = this.calculateDealBracketAmounts();
      bracketResultForecast = this.calculateDealBracketAmounts('forecast');
      bracketResultActual = this.calculateDealBracketAmounts('actual');
      artistGuarantee = bracketResult.totalGuarantee;
    }

    this.bracketResultsByMode = {
      offer: bracketResult,
      forecast: bracketResultForecast,
      actual: bracketResultActual,
    };

    const expensesTotal = new Decimal(this.expensesSection.externalTotal || 0);
    const expensesForecastTotal = new Decimal(this.expensesSection.externalForecastTotal || 0);
    const expensesActualTotal = new Decimal(this.expensesSection.externalActualTotal || 0);

    let promoterProfit = new Decimal(0);
    let promoterProfitForecast = new Decimal(0);
    let promoterProfitActual = new Decimal(0);
    if (this.offer.promoter_profit) {
      const pct = this.parseMaskedInput(this.offer.promoter_profit_percentage);
      const allExpenses = expensesTotal.plus(artistGuarantee);
      const allExpensesForecast = expensesForecastTotal.plus(artistGuarantee);
      const allExpensesActual = expensesActualTotal.plus(artistGuarantee);
      promoterProfit = allExpenses.times(pct).dividedBy(100);
      promoterProfitForecast = allExpensesForecast.times(pct).dividedBy(100);
      promoterProfitActual = allExpensesActual.times(pct).dividedBy(100);
    }

    const showNetAfterPromoterProfit = showNetAfterExpenses.minus(promoterProfit);
    const showNetAfterPromoterProfitForecast = showNetAfterExpensesForecast.minus(promoterProfitForecast);
    const showNetAfterPromoterProfitActual = showNetAfterExpensesActual.minus(promoterProfitActual);

    const splitPoint = artistGuarantee.plus(expensesTotal).plus(promoterProfit);
    const splitPointForecast = artistGuarantee.plus(expensesForecastTotal).plus(promoterProfitForecast);
    const splitPointActual = artistGuarantee.plus(expensesActualTotal).plus(promoterProfitActual);

    const subtractFromShowNet = dollarOrPercentageOperator === 'AND' ? artistGuarantee : new Decimal(0);

    const toBeSharedAmount = Decimal.max(0, showNetAfterPromoterProfit.minus(subtractFromShowNet));
    const toBeSharedAmountForecast = Decimal.max(0, showNetAfterPromoterProfitForecast.minus(subtractFromShowNet));
    const toBeSharedAmountActual = Decimal.max(0, showNetAfterPromoterProfitActual.minus(subtractFromShowNet));

    let artistPercentage = new Decimal(0);
    let artistPercentageForecast = new Decimal(0);
    let artistPercentageActual = new Decimal(0);

    const revenueBaseOffer = this.getRevenueBase('offer');
    const revenueBaseForecast = this.getRevenueBase('forecast');
    const revenueBaseActual = this.getRevenueBase('actual');

    // A zero threshold counts as unset: the Simple editor persisted blank
    // breakevens as 0 (O-4981), and a $0 gate must not flip the upside base
    // from to-be-shared to raw gross.
    const breakevenSet = this.offer.breakeven_threshold != null &&
      this.parseMaskedInput(this.offer.breakeven_threshold).greaterThan(0);
    const gateBaseOffer = revenueBaseOffer !== null
      ? revenueBaseOffer
      : (breakevenSet ? new Decimal(this.grossPotentialForMode('offer')) : toBeSharedAmount);
    const gateBaseForecast = revenueBaseForecast !== null
      ? revenueBaseForecast
      : (breakevenSet ? new Decimal(this.grossPotentialForMode('forecast')) : toBeSharedAmountForecast);
    const gateBaseActual = revenueBaseActual !== null
      ? revenueBaseActual
      : (breakevenSet ? new Decimal(this.grossPotentialForMode('actual')) : toBeSharedAmountActual);

    if (fromDollarOne) {
      const baseOffer = this.applyBreakevenGate(gateBaseOffer);
      const baseForecast = this.applyBreakevenGate(gateBaseForecast);
      const baseActual = this.applyBreakevenGate(gateBaseActual);
      artistPercentage = upsidePercentage.dividedBy(100).times(baseOffer);
      artistPercentageForecast = upsidePercentageForecast.dividedBy(100).times(baseForecast);
      artistPercentageActual = upsidePercentageActual.dividedBy(100).times(baseActual);
    } else if (bracketResult) {
      artistPercentage = bracketResult.totalPercentage;
      artistPercentageForecast = bracketResultForecast.totalPercentage;
      artistPercentageActual = bracketResultActual.totalPercentage;
    } else if (upsidePercentage.greaterThan(0)) {
      const baseOffer = this.applyBreakevenGate(gateBaseOffer);
      const baseForecast = this.applyBreakevenGate(gateBaseForecast);
      const baseActual = this.applyBreakevenGate(gateBaseActual);
      artistPercentage = baseOffer.times(upsidePercentage).dividedBy(100);
      artistPercentageForecast = baseForecast.times(upsidePercentageForecast).dividedBy(100);
      artistPercentageActual = baseActual.times(upsidePercentageActual).dividedBy(100);
    }

    const breakevenThreshold = this.offer.breakeven_threshold != null
      ? this.parseMaskedInput(this.offer.breakeven_threshold)
      : null;
    const breakevenBase = bracketResult ? bracketResult.aggregateRevenue : gateBaseOffer;
    const breakevenToBeSharedAmount = breakevenThreshold !== null && !bracketResult
      ? this.applyBreakevenGate(gateBaseOffer)
      : null;
    const breakevenInEffect = breakevenSet && (
      bracketResult ? bracketResult.percentageInEffect : upsidePercentage.greaterThan(0)
    );
    const percentageHasSingleBase = fromDollarOne || (!bracketResult && upsidePercentage.greaterThan(0));
    const preSplitDeductionsFor = (mode, upside, revenueBase, showNetAfterPromoterProfitForMode) => (
      percentageHasSingleBase && upside.greaterThan(0)
        ? this.preSplitDeductionsAgainstBase(mode, revenueBase, showNetAfterPromoterProfitForMode.minus(subtractFromShowNet), breakevenSet)
        : null
    );

    return {
      ...this.showSummary,
      preSplitDeductions: preSplitDeductionsFor('offer', upsidePercentage, revenueBaseOffer, showNetAfterPromoterProfit),
      preSplitDeductionsForecast: preSplitDeductionsFor('forecast', upsidePercentageForecast, revenueBaseForecast, showNetAfterPromoterProfitForecast),
      preSplitDeductionsActual: preSplitDeductionsFor('actual', upsidePercentageActual, revenueBaseActual, showNetAfterPromoterProfitActual),
      artistGuarantee: artistGuarantee.toNumber(),
      artistPercentage: artistPercentage.toNumber(),
      artistPercentageForecast: artistPercentageForecast.toNumber(),
      artistPercentageActual: artistPercentageActual.toNumber(),
      upsidePercentage: upsidePercentage.toNumber(),
      dollarOrPercentageOperator,
      fromDollarOne,
      promoterProfit: promoterProfit.toNumber(),
      promoterProfitForecast: promoterProfitForecast.toNumber(),
      promoterProfitActual: promoterProfitActual.toNumber(),
      showNetAfterPromoterProfit: showNetAfterPromoterProfit.toNumber(),
      showNetAfterPromoterProfitForecast: showNetAfterPromoterProfitForecast.toNumber(),
      showNetAfterPromoterProfitActual: showNetAfterPromoterProfitActual.toNumber(),
      splitPoint: splitPoint.toNumber(),
      splitPointForecast: splitPointForecast.toNumber(),
      splitPointActual: splitPointActual.toNumber(),
      toBeSharedAmount: toBeSharedAmount.toNumber(),
      toBeSharedAmountForecast: toBeSharedAmountForecast.toNumber(),
      toBeSharedAmountActual: toBeSharedAmountActual.toNumber(),
      breakevenThreshold: breakevenThreshold === null ? null : breakevenThreshold.toNumber(),
      breakevenBase: breakevenBase.toNumber(),
      breakevenToBeSharedAmount: breakevenToBeSharedAmount === null ? null : breakevenToBeSharedAmount.toNumber(),
      breakevenInEffect,
    };
  }

  calculateDepositsSection() {
    let totalDeposited = new Decimal(0);
    const items = [];

    for (const deposit of this.deposits) {
      if (!deposit.paid) continue;
      const amount = this.parseMaskedInput(deposit.amount || deposit.value);
      totalDeposited = totalDeposited.plus(amount);
      items.push({
        ...deposit,
        calculatedAmount: amount.toNumber(),
      });
    }

    return {
      items,
      total: totalDeposited.toNumber(),
    };
  }

  calculateArtistEarnings(mode) {
    const guarantee = new Decimal(this.showSummary.artistGuarantee || 0);
    const percentage = mode === 'actual'
      ? new Decimal(this.showSummary.artistPercentageActual || 0)
      : mode === 'forecast'
        ? new Decimal(this.showSummary.artistPercentageForecast || 0)
        : new Decimal(this.showSummary.artistPercentage || 0);
    const operator = this.showSummary.dollarOrPercentageOperator;

    let artistGuaranteeAndOrPercentage;
    if (operator === 'OR') {
      artistGuaranteeAndOrPercentage = Decimal.max(guarantee, percentage);
    } else {
      artistGuaranteeAndOrPercentage = guarantee.plus(percentage);
    }

    const rawPayout = artistGuaranteeAndOrPercentage;
    const floor = this.offer.artist_payout_floor != null
      ? this.parseMaskedInput(this.offer.artist_payout_floor)
      : null;
    const cap = this.parseCapOrNull(this.offer.artist_payout_cap);
    let payoutFloored = false;
    let payoutCapped = false;
    if (floor !== null && artistGuaranteeAndOrPercentage.lessThan(floor)) {
      artistGuaranteeAndOrPercentage = floor;
      payoutFloored = true;
    }
    if (cap !== null && artistGuaranteeAndOrPercentage.greaterThan(cap)) {
      artistGuaranteeAndOrPercentage = cap;
      payoutCapped = true;
    }

    let capOverflow = new Decimal(0);
    let capOverflowTarget = null;
    if (payoutCapped && cap !== null) {
      capOverflow = rawPayout.minus(cap);
      const bracketResultForMode = this.bracketResultsByMode?.[mode || 'offer'];
      const perBracket = bracketResultForMode?.perBracket || [];
      if (perBracket.length > 0) {
        let cumulative = new Decimal(0);
        for (const entry of perBracket) {
          cumulative = cumulative.plus(entry.payout);
          if (cumulative.greaterThan(cap)) {
            capOverflowTarget = entry.capOverflowTarget;
            break;
          }
        }
      }
    }

    const totalAdditions = mode === 'actual'
      ? new Decimal(this.additionsSection.externalActualTotal || 0)
      : mode === 'forecast'
        ? new Decimal(this.additionsSection.externalForecastTotal || 0)
        : new Decimal(this.additionsSection.externalTotal || 0);
    const totalDeductions = mode === 'actual'
      ? new Decimal(this.deductionsSection.externalActualTotal || 0)
      : mode === 'forecast'
        ? new Decimal(this.deductionsSection.externalForecastTotal || 0)
        : new Decimal(this.deductionsSection.externalTotal || 0);

    let artistTotalDue = Decimal.max(
      new Decimal(0),
      artistGuaranteeAndOrPercentage
        .plus(totalAdditions)
        .minus(totalDeductions)
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
    );

    let taxWithholding = new Decimal(0);
    if (this.offer.has_tax_withholding) {
      const threshold = this.parseMaskedInput(this.offer.tax_withholding_minimum_threshold);
      const percentage = this.parseMaskedInput(this.offer.tax_withholding_percentage);
      const totalDeposits = this.isSettling ? new Decimal(this.depositsSection.total || 0) : new Decimal(0);
      const taxableArtistIncome = Decimal.max(
        new Decimal(0),
        artistTotalDue.minus(this.exemptFromTaxWithholdingTotal(mode))
      ).plus(totalDeposits);
      if (taxableArtistIncome.greaterThan(threshold)) {
        taxWithholding = taxableArtistIncome.times(percentage).dividedBy(100);
      }
    }

    const artistSettlementDue = artistTotalDue.minus(taxWithholding);
    const totalDeposits = new Decimal(this.depositsSection.total || 0);
    const remainingDue = artistSettlementDue.minus(totalDeposits);
    const ticketsToBreakevenDetail = this.ticketsToBreakevenDetail(mode);

    return {
      artistGuaranteeAndOrPercentage: artistGuaranteeAndOrPercentage.toNumber(),
      rawPayout: rawPayout.toNumber(),
      payoutCapped,
      payoutFloored,
      capOverflow: capOverflow.toNumber(),
      capOverflowTarget,
      totalAdditions: totalAdditions.toNumber(),
      totalDeductions: totalDeductions.toNumber(),
      taxWithholding: taxWithholding.toNumber(),
      artistTotalDue: artistTotalDue.toNumber(),
      artistSettlementDue: artistSettlementDue.toNumber(),
      totalDeposits: totalDeposits.toNumber(),
      remainingDue: remainingDue.toNumber(),
      ticketsToBreakevenDetail: ticketsToBreakevenDetail,
    };
  }

  // ------------------------------------------------------------------------
  // Pool / multi-artist allocation. The "members" are pool member offers
  // (this.offer.pool_members). With 0 or 1 member the offer behaves exactly as a
  // single-offer deal (master) and earnings are left untouched. With 2+ members
  // the offer-level door-deal payout (or the raw revenue base, in independent
  // mode) is split among the members by share + payout_strategy.
  // ------------------------------------------------------------------------

  isSingleArtistMode() {
    // Length 0 = no pool overlay at all (Independent mode) → the offer's own
    // single-offer earnings.
    if (this.poolMembers.length === 0) return true;
    // A lone member takes the whole pool ONLY when explicitly set to 'remainder'.
    // An unset (null) strategy behaves as EXPLICIT — consistent with the
    // multi-member allocation — so the member's pool_percentage is applied. null
    // does NOT mean "take everything": the default (explicit, 0%) is $0, and a
    // member the user set to 50% gets 50% of the pool, even as the only artist.
    if (this.poolMembers.length === 1) {
      const strategy = this.poolMembers[0].payout_strategy || this.poolMembers[0].payoutStrategy;
      return strategy === 'remainder';
    }
    return false;
  }

  artistEarningsForMode(mode) {
    return mode === 'actual'
      ? this.artistEarningsActual
      : mode === 'forecast'
        ? this.artistEarningsForecast
        : this.artistEarnings;
  }

  calculatePoolMembers(mode) {
    const earnings = this.artistEarningsForMode(mode);

    if (this.offer.pool_mode === true && this.poolMembers.length > 0) {
      earnings.grossPotential = this.grossPotentialForMode(mode);
      earnings.dealBracketReached = this.dealBracketReached(mode);
    }

    if (this.isSingleArtistMode()) {
      const member = this.poolMembers[0] || {};
      const performerId = member.performer_id != null ? member.performer_id : (member.performerId != null ? member.performerId : null);

      // O-4755. A lone member on `remainder` takes the whole pool, so the pool IS
      // the door-deal payout — publish it here as well as on the multi-member
      // path, or a one-artist pool reads "Pool total $0" and the gross→pool
      // converter answers "set the pool deal to convert" to a booker who has
      // just set it.
      const isPool = this.poolMembers.length > 0 && this.offer.pool_mode === true;
      if (isPool) earnings.poolAmount = earnings.artistTotalDue;

      // A lone member still has their OWN cap and floor, and this PR puts both in
      // front of the booker. Taking the offer-level earnings wholesale ignored
      // them: a support act typed "cap $250" and the preview paid the whole pool.
      //
      // The clamp has to move the OFFER's totals too, not just the member line.
      // Returning a clamped line on its own left the member showing $250 while
      // artistTotalDue and artistSettlementDue still read $20,000 — and
      // V3Offer#total_due evaluates this file server-side and publishes that
      // figure to the settlement card, the confirm show page, the offers
      // controller and the admin blueprint. Venue Net was wrong by the clamped
      // amount at the same time, on the same screen.
      const memberCap = this.parseCapOrNull(member.artist_payout_cap);
      const memberFloor = member.artist_payout_floor != null
        ? this.parseMaskedInput(member.artist_payout_floor)
        : null;
      if (memberCap !== null || memberFloor !== null) {
        const totalDue = new Decimal(earnings.artistTotalDue);
        const exemptTotal = this.exemptFromTaxWithholdingTotal(mode);
        const takeWithoutExempt = exemptTotal.greaterThan(0)
          ? this.buildRemainderResult(member, Decimal.max(new Decimal(0), totalDue.minus(exemptTotal))).take
          : null;
        const clamped = this.buildRemainderResult(member, totalDue, takeWithoutExempt);

        earnings.artistTotalDue = clamped.take.toNumber();
        earnings.artistSettlementDue = clamped.settlementTake.toNumber();
        earnings.taxWithholding = clamped.take.minus(clamped.settlementTake).toNumber();
        earnings.remainingDue = clamped.settlementTake
          .minus(new Decimal(this.depositsSection.total || 0)).toNumber();
        earnings.payoutCapped = clamped.capped;
        earnings.payoutFloored = clamped.floored;

        return [{
          performerId,
          take: clamped.take.toNumber(),
          settlementTake: clamped.settlementTake.toNumber(),
          rawTake: clamped.rawTake.toNumber(),
          capped: clamped.capped,
          floored: clamped.floored,
          breakevenMet: true,
          capOverflow: clamped.capOverflow.toNumber(),
          capOverflowRecipients: [],
          capOverflowRetained: this.capOverflowExcess(clamped).toNumber(),
        }];
      }

      return [{
        performerId,
        take: earnings.artistTotalDue,
        settlementTake: earnings.artistSettlementDue,
        rawTake: earnings.rawPayout,
        capped: earnings.payoutCapped,
        floored: earnings.payoutFloored,
        breakevenMet: true,
        capOverflow: earnings.capOverflow,
        capOverflowRecipients: [],
        capOverflowRetained: Decimal.max(new Decimal(0), new Decimal(earnings.capOverflow))
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber(),
      }];
    }

    // Pool mode: the co-headliners split the offer-level door-deal payout
    // (guarantee / deal brackets + percentage + cap/floor/breakeven, already
    // computed by calculateArtistEarnings into earnings.artistTotalDue before this
    // runs). Independent mode: each member earns independently off the raw revenue
    // base, exactly as before.
    const poolMode = this.offer.pool_mode === true;
    const pool = poolMode
      ? new Decimal(earnings.artistTotalDue)
      : new Decimal(this.getRevenueBaseOrToBeShared(mode));
    const allocation = this.computePoolMemberAllocation(this.offer, this.poolMembers, pool, { mode, poolMode });

    let totalDue = new Decimal(0);
    let settlementDue = new Decimal(0);
    const sortedMembers = [...this.poolMembers].sort((a, b) => (a.position || 0) - (b.position || 0));
    const result = sortedMembers.map((member) => {
      const performerId = member.performer_id != null ? member.performer_id : (member.performerId != null ? member.performerId : null);
      const memberResult = allocation.takes.get(performerId);
      totalDue = totalDue.plus(memberResult.take);
      settlementDue = settlementDue.plus(memberResult.settlementTake);
      return {
        performerId,
        take: memberResult.take.toNumber(),
        settlementTake: memberResult.settlementTake.toNumber(),
        rawTake: memberResult.rawTake.toNumber(),
        capped: memberResult.capped,
        floored: memberResult.floored,
        breakevenMet: memberResult.breakevenMet,
        capOverflow: memberResult.capOverflow.toNumber(),
        capOverflowRecipients: memberResult.capOverflowRecipients.map((recipient) => ({
          performerId: recipient.performerId,
          amount: recipient.amount.toNumber(),
        })),
        capOverflowRetained: memberResult.capOverflowRetained.toNumber(),
      };
    });

    earnings.artistTotalDue = totalDue.toNumber();
    earnings.artistSettlementDue = settlementDue.toNumber();
    // O-2783 settlement: the displayed Tax Withholding line is offer-level,
    // computed once on the whole pool — but the settlement TOTAL is the sum of
    // per-member post-tax takes (threshold + apportioned deposits applied per
    // member). Re-derive the withholding as the per-member sum (= pre-tax pool
    // minus post-tax pool) so the statement foots: artistTotalDue − taxWithholding
    // == artistSettlementDue by construction.
    earnings.taxWithholding = totalDue.minus(settlementDue).toNumber();
    earnings.remainingDue = settlementDue.minus(new Decimal(this.depositsSection.total || 0)).toNumber();
    earnings.oversubscribed = allocation.oversubscribed;
    earnings.unallocated = allocation.unallocated || 0;
    // The amount actually being divided among the members (in Pool mode this is the
    // door-deal payout, not the raw revenue base) so the allocation strip can label
    // "Pool" with the number it is splitting.
    earnings.poolAmount = pool.toNumber();

    return result;
  }

  getRevenueBaseOrToBeShared(mode) {
    const revenueBase = this.getRevenueBase(mode);
    if (revenueBase !== null) return revenueBase.toNumber();
    const sharedKey = mode === 'actual'
      ? 'toBeSharedAmountActual'
      : mode === 'forecast'
        ? 'toBeSharedAmountForecast'
        : 'toBeSharedAmount';
    return this.showSummary[sharedKey] || 0;
  }

  memberBreakevenGate(baseAmount, threshold) {
    if (threshold == null) return baseAmount;
    const parsed = this.parseMaskedInput(threshold);
    return Decimal.max(new Decimal(0), baseAmount.minus(parsed));
  }

  computeMemberPayout(member, ctx) {
    if (ctx.poolMode) {
      return this.computePoolMemberPayout(member, ctx);
    }
    const guarantee = this.parseMaskedInput(member.guarantee != null ? member.guarantee : this.offer.dollar_amount);
    const upsidePercentage = this.parseMaskedInput(member.percentage != null ? member.percentage : this.offer.upside_percentage);
    const operator = member.dollar_or_percentage_operator || this.offer.dollar_or_percentage_operator || 'DOLLAR';

    const breakeven = member.breakeven_threshold != null ? member.breakeven_threshold : this.offer.breakeven_threshold;
    const gatedBase = this.memberBreakevenGate(new Decimal(ctx.revenueBase), breakeven);
    const breakevenMet = breakeven == null || gatedBase.greaterThan(0);
    const percentage = upsidePercentage.dividedBy(100).times(gatedBase);

    let combined;
    if (operator === 'OR') {
      combined = Decimal.max(guarantee, percentage);
    } else {
      combined = guarantee.plus(percentage);
    }

    const rawTake = combined;
    const floor = member.artist_payout_floor != null
      ? this.parseMaskedInput(member.artist_payout_floor)
      : (this.offer.artist_payout_floor != null ? this.parseMaskedInput(this.offer.artist_payout_floor) : null);
    // 0 means "no cap", exactly as blank does — so it must also fall back to the
    // offer's cap, not sit between the two readings. Testing `!= null` made a
    // member cap of 0 mean "no cap AND ignore the offer cap": with an offer cap
    // of $4,000 a blank member took $4,000 and a 0 member took $10,000, a third
    // answer matching neither reading of the rule.
    const cap = this.parseCapOrNull(member.artist_payout_cap)
      ?? this.parseCapOrNull(this.offer.artist_payout_cap);

    let floored = false;
    let capped = false;
    if (floor !== null && combined.lessThan(floor)) {
      combined = floor;
      floored = true;
    }
    if (cap !== null && combined.greaterThan(cap)) {
      combined = cap;
      capped = true;
    }

    const capOverflow = (capped && cap !== null) ? rawTake.minus(cap) : new Decimal(0);
    const take = Decimal.max(new Decimal(0), combined.toDecimalPlaces(2, Decimal.ROUND_HALF_UP));
    const settlementTake = take.minus(this.memberTaxWithholding(member, take));

    return { take, rawTake, capped, floored, breakevenMet, capOverflow, settlementTake };
  }

  computePoolMemberPayout(member, ctx) {
    const guarantee = this.parseMaskedInput(member.guarantee != null ? member.guarantee : 0);
    const upsidePercentage = this.parseMaskedInput(member.percentage != null ? member.percentage : 0);
    const operator = member.dollar_or_percentage_operator || this.offer.dollar_or_percentage_operator || 'DOLLAR';

    const strategy = member.payout_strategy || member.payoutStrategy;
    const shareBase = strategy === 'percentage_of_gross'
      ? new Decimal(this.grossPotentialForMode(ctx.mode))
      : new Decimal(ctx.revenueBase);
    const percentage = upsidePercentage.dividedBy(100).times(shareBase);

    let combined = operator === 'OR'
      ? Decimal.max(guarantee, percentage)
      : guarantee.plus(percentage);

    const rawTake = combined;
    const floor = member.artist_payout_floor != null ? this.parseMaskedInput(member.artist_payout_floor) : null;
    const cap = this.parseCapOrNull(member.artist_payout_cap);

    let floored = false;
    let capped = false;
    if (floor !== null && combined.lessThan(floor)) {
      combined = floor;
      floored = true;
    }
    if (cap !== null && combined.greaterThan(cap)) {
      combined = cap;
      capped = true;
    }

    const capOverflow = (capped && cap !== null) ? rawTake.minus(cap) : new Decimal(0);
    const take = Decimal.max(new Decimal(0), combined.toDecimalPlaces(2, Decimal.ROUND_HALF_UP));
    const settlementTake = take.minus(this.memberTaxWithholding(member, take));

    return { take, rawTake, capped, floored, breakevenMet: true, capOverflow, settlementTake };
  }

  memberTaxWithholding(member, take, { depositShare, exemptShare } = {}) {
    const hasWithholding = member.has_tax_withholding != null ? member.has_tax_withholding : this.offer.has_tax_withholding;
    if (!hasWithholding) return new Decimal(0);
    const threshold = this.parseMaskedInput(member.tax_withholding_minimum_threshold != null ? member.tax_withholding_minimum_threshold : this.offer.tax_withholding_minimum_threshold);
    const percentage = this.parseMaskedInput(member.tax_withholding_percentage != null ? member.tax_withholding_percentage : this.offer.tax_withholding_percentage);
    // The pool's deposit is a SINGLE prepayment against the whole pool. The
    // caller (computePoolMemberAllocation) apportions it across members by their
    // take and passes this member's share, so the deposit is counted ONCE across
    // the pool — not the full total per member (which over-withheld). When no
    // share is provided (legacy/non-pool call, result overwritten) it falls back
    // to the full settling total.
    const deposits = depositShare != null
      ? new Decimal(depositShare)
      : (this.isSettling ? new Decimal(this.depositsSection.total || 0) : new Decimal(0));
    const exempt = exemptShare != null ? new Decimal(exemptShare) : new Decimal(0);
    const income = Decimal.max(new Decimal(0), take.minus(exempt)).plus(deposits);
    if (income.greaterThan(threshold)) {
      return income.times(percentage).dividedBy(100);
    }
    return new Decimal(0);
  }

  exemptFromTaxWithholdingTotal(mode) {
    const totalKey = mode === 'actual'
      ? 'calculatedActualTotal'
      : mode === 'forecast'
        ? 'calculatedForecastTotal'
        : 'calculatedTotal';
    return this.additionsSection.items
      .filter((item) => item.exempt_from_tax_withholding && (item.show_in_offer ?? true))
      .reduce((summed, item) => summed.plus(Decimal.max(new Decimal(0), new Decimal(item[totalKey] || 0))), new Decimal(0));
  }

  memberExemptShares(members, takes, takesWithoutExempt, exemptGrowth) {
    const shares = new Map();
    let sharesTotal = new Decimal(0);
    for (const member of members) {
      const key = this.memberPerformerId(member);
      const withoutExempt = takesWithoutExempt.get(key);
      const share = withoutExempt
        ? Decimal.max(new Decimal(0), takes.get(key).take.minus(withoutExempt.take))
        : new Decimal(0);
      shares.set(key, share);
      sharesTotal = sharesTotal.plus(share);
    }
    if (!sharesTotal.greaterThan(exemptGrowth)) return shares;
    const scale = exemptGrowth.dividedBy(sharesTotal);
    for (const [key, share] of shares) shares.set(key, share.times(scale));
    return shares;
  }

  computePoolMemberAllocation(offer, members, pool, {
    mode, poolMode, attributeExemption = true, attributeOverflow = true, heldBackFromLeftover = new Decimal(0),
  }) {
    const ctx = { mode, revenueBase: pool.toNumber(), poolMode };
    const takes = new Map();

    const explicitMembers = [];
    const remainderMembers = [];
    const equalShareMembers = [];
    // O-4756. A stated share of what is LEFT, as opposed to `explicit` (a share
    // of the whole pool) or `equal_share_of_remainder` (an even split of the
    // leftover). Black Cat's shape: the opener takes a flat guarantee, then the
    // headliner and co-headliner split the rest 60/40.
    const percentageOfRemainderMembers = [];
    const unknownStrategyMembers = [];
    for (const member of members) {
      const strategy = member.payout_strategy || member.payoutStrategy || 'explicit';
      if (strategy === 'remainder') {
        remainderMembers.push(member);
      } else if (strategy === 'equal_share_of_remainder') {
        equalShareMembers.push(member);
      } else if (strategy === 'percentage_of_remainder') {
        percentageOfRemainderMembers.push(member);
      } else if (strategy === 'explicit' || strategy === 'percentage_of_gross') {
        explicitMembers.push(member);
      } else {
        // An UNRECOGNISED strategy must not land in the explicit bucket, whose
        // percentage is read against the whole pool: a client bundle held across
        // a deploy, or a strategy shipped Ruby-first, would then pay a member
        // their share of the ENTIRE pool. With the editor seeding 100 on
        // selection that is the whole pool to one artist and $0 to everyone
        // else. Route it to the guarantee-only safety net below instead.
        unknownStrategyMembers.push(member);
      }
    }

    let explicitTotal = new Decimal(0);
    for (const member of explicitMembers) {
      const result = this.computeMemberPayout(member, ctx);
      takes.set(this.memberPerformerId(member), result);
      explicitTotal = explicitTotal.plus(result.take);
    }

    // O-4756 (ask 6, "guarantee vs back-end"). A member on `percentage_of_remainder`
    // whose deal type is "versus" takes the GREATER of their guarantee and their
    // share of the leftover, rather than both added together.
    //
    // Their guarantee is still RESERVED out of the pool like any other. An
    // earlier cut skipped the reservation, reasoning that an alternative is not
    // a claim — but a guarantee that WINS is then paid out of a leftover every
    // other member's share was measured against in full, and the pool overdraws.
    // On a $10,000 pool a $6,000 versus guarantee beating a $4,875 share paid
    // $11,125 in total; where the sum still fell under the pool it moved money
    // from venue net to an artist with no warning at all, because
    // `oversubscribed` only fires on over-allocation. Reserving it costs the
    // versus member a slightly smaller leftover to measure against, and that is
    // the safe side of the trade: the pool always foots.
    //
    // Deliberately scoped to this strategy. `remainder` and
    // `equal_share_of_remainder` also ignore the operator today, but they are
    // shipped with production data behind them and changing how they settle is
    // not this feature's business.
    const usesVersus = (member) => (
      (member.dollar_or_percentage_operator || this.offer.dollar_or_percentage_operator) === 'OR'
    );

    let remainderGuaranteeTotal = new Decimal(0);
    const remainderResults = new Map();
    for (const member of remainderMembers.concat(equalShareMembers, percentageOfRemainderMembers, unknownStrategyMembers)) {
      const guarantee = this.parseMaskedInput(member.guarantee != null ? member.guarantee : 0);
      remainderGuaranteeTotal = remainderGuaranteeTotal.plus(guarantee);
      remainderResults.set(this.memberPerformerId(member), guarantee);
    }

    let leftover = pool.minus(explicitTotal).minus(heldBackFromLeftover).minus(remainderGuaranteeTotal);
    let oversubscribed = false;
    if (leftover.lessThan(0)) {
      leftover = new Decimal(0);
      oversubscribed = true;
    }

    // Both distributors below settle to the cent by handing the reconciling
    // remainder to the LAST member, so who is last decides who gets it. The two
    // producers disagree on order — member_json sorts by [position, offer id]
    // while the editor's lineup buckets headliners first — so impose one here
    // rather than inheriting whichever called us. `position` is the only key
    // both sides share; the member key breaks a tie.
    const orderResidualMembers = (members2) => [...members2].sort((a, b) => (
      (a.position || 0) - (b.position || 0)
      || (this.memberPerformerId(a) || 0) - (this.memberPerformerId(b) || 0)
    ));

    // Split the leftover EVENLY across the residual members. The last member
    // absorbs the rounding residual so the shares sum to `leftover` exactly — no
    // penny drift leaking into venue net (each take is independently
    // 2dp-rounded). A single member therefore still takes the whole leftover
    // (byte-identical to the prior behavior). Dividing by count also guards a
    // >1-"remainder" race (I4): two remainder members split the leftover instead
    // of each taking it all. Non-last shares round DOWN so the last is never
    // negative.
    const distributeRemainder = (unorderedMembers) => {
      const residualMembers = orderResidualMembers(unorderedMembers);
      const count = residualMembers.length;
      let allocated = new Decimal(0);
      residualMembers.forEach((member, idx) => {
        const guarantee = remainderResults.get(this.memberPerformerId(member));
        const share = idx === count - 1
          ? leftover.minus(allocated)
          : leftover.dividedBy(count).toDecimalPlaces(2, Decimal.ROUND_DOWN);
        allocated = allocated.plus(share);
        takes.set(this.memberPerformerId(member), this.buildRemainderResult(member, guarantee.plus(share)));
      });
    };

    // O-4756. Each member takes their STATED share of the leftover — 60/40 pays
    // 60/40. Shares are read literally rather than normalised as weights, which
    // is how `explicit` already behaves, so a typed number is never silently
    // rescaled into a different payout. Under 100 leaves the shortfall with the
    // venue; over 100 overdraws and is flagged below.
    const distributePercentageOfRemainder = (unorderedMembers) => {
      const residualMembers = orderResidualMembers(unorderedMembers);

      const statedFor = (member) => new Decimal(
        this.parseMaskedInput(member.percentage != null ? member.percentage : 0)
      );
      const statedTotal = residualMembers.reduce((sum, m) => sum.plus(statedFor(m)), new Decimal(0));

      // Stated shares over 100 are NOT flagged here. Whether the pool is actually
      // overdrawn is decided against the final takes below, after caps, floors
      // and guarantees have had their say — shares summing over 100 frequently
      // do not overdraw anything (a cap clamps them back, or the leftover is
      // zero so every share computes to $0), and flagging on the stated figure
      // produced far more false alarms than real ones.
      const sharesFrom = (base) => {
        const out = residualMembers.map((member) => (
          base.times(statedFor(member)).dividedBy(100).toDecimalPlaces(2, Decimal.ROUND_DOWN)
        ));

        // Reconcile rounding ONLY when the shares claim the whole base. Doing it
        // unconditionally would hand the last member the undistributed portion of
        // an under-100 split, contradicting the literal reading above.
        //
        // The cent goes to the last member with a NON-ZERO STATED share. A support
        // act deliberately set to 0% is claiming nothing, and keying on the
        // COMPUTED share would drop the cent entirely when every share rounds
        // down — leaking it to venue net on a split that states exactly 100%.
        if (statedTotal.equals(100) && out.length > 0) {
          let target = -1;
          for (let i = residualMembers.length - 1; i >= 0; i -= 1) {
            if (statedFor(residualMembers[i]).greaterThan(0)) { target = i; break; }
          }
          if (target >= 0) {
            const allocated = out.reduce((sum, sh) => sum.plus(sh), new Decimal(0));
            out[target] = out[target].plus(base.minus(allocated));
          }
        }
        return out;
      };

      // "versus" pays the greater of the guarantee and the share, "plus" pays
      // both — matching how the same operator already behaves for an `explicit`
      // member in computePoolMemberPayout.
      //
      // A versus guarantee is RESERVED out of the pool like any other, so the
      // pool always foots. But when the SHARE wins, that reservation was never
      // needed, and leaving it reserved shrinks the base everyone else measures
      // against — money the shares claim silently becomes venue net, since
      // `oversubscribed` only fires on OVER-allocation.
      //
      // So release the guarantee of every member whose share won, and divide
      // again. This must run to a FIXED POINT, not once: releasing raises the
      // base, a raised base raises every share, and that can push a member who
      // LOST the first pass into winning the second — whose guarantee then has
      // to be released too. Two vs members on a $10,000 pool at 50/50 with
      // $2,000 and $3,000 guarantees settled $3,500 each under a single pass;
      // the $3,000 member only wins after the first release, and iterating pays
      // the correct $5,000 each.
      //
      // It terminates in at most one pass per member: the winners set only ever
      // grows (the base rises monotonically, so every share rises, while the
      // guarantees are fixed), so a repeated release total means we are done.
      const guaranteeOf = (member) => remainderResults.get(this.memberPerformerId(member));
      const shareWins = (member, shareList, idx) => (
        usesVersus(member) && shareList[idx].greaterThan(guaranteeOf(member))
      );
      const releasedFrom = (shareList) => residualMembers.reduce(
        (sum, member, idx) => (shareWins(member, shareList, idx) ? sum.plus(guaranteeOf(member)) : sum),
        new Decimal(0),
      );

      let shares = sharesFrom(leftover);
      let released = new Decimal(0);
      for (let pass = 0; pass <= residualMembers.length; pass += 1) {
        const next = releasedFrom(shares);
        if (next.equals(released)) break;
        released = next;
        shares = sharesFrom(leftover.plus(released));
      }

      residualMembers.forEach((member, idx) => {
        const combined = usesVersus(member)
          ? Decimal.max(guaranteeOf(member), shares[idx])
          : guaranteeOf(member).plus(shares[idx]);
        takes.set(this.memberPerformerId(member), this.buildRemainderResult(member, combined));
      });
    };

    if (remainderMembers.length > 0) {
      distributeRemainder(remainderMembers);
    } else if (equalShareMembers.length > 0) {
      distributeRemainder(equalShareMembers);
    } else if (percentageOfRemainderMembers.length > 0) {
      distributePercentageOfRemainder(percentageOfRemainderMembers);
    }

    // These branches are exclusive because the leftover can only be claimed
    // once, so in a pool that mixes residual strategies the losing members get
    // no entry above. The model rejects such a pool, but it can still be
    // assembled around that guard (the strategy is writable on an offer that is
    // only later swept into a pool), and a missing entry reads as `undefined`
    // downstream — the settlement view calls the engine inside a useMemo with no
    // try/catch, so one absent member takes the whole finance page down. Give
    // every residual member their guarantee so nothing is ever missing; the
    // leftover itself stays with whichever branch won.
    for (const member of remainderMembers.concat(equalShareMembers, percentageOfRemainderMembers, unknownStrategyMembers)) {
      const key = this.memberPerformerId(member);
      if (takes.has(key)) continue;
      takes.set(key, this.buildRemainderResult(member, remainderResults.get(key) || new Decimal(0)));
    }

    const totalDeposits = this.isSettling ? new Decimal(this.depositsSection.total || 0) : new Decimal(0);
    const totalExempt = poolMode && attributeExemption ? this.exemptFromTaxWithholdingTotal(mode) : new Decimal(0);
    const takesWithoutExempt = totalExempt.greaterThan(0)
      ? this.computePoolMemberAllocation(
        offer, members, Decimal.max(new Decimal(0), pool.minus(totalExempt)),
        { mode, poolMode, attributeExemption: false, attributeOverflow: false }
      ).takes
      : new Map();
    const shareableMembers = members.filter((member) => takes.get(this.memberPerformerId(member)));
    let sumTakes = new Decimal(0);
    for (const allocation of takes.values()) sumTakes = sumTakes.plus(allocation.take);
    const exemptGrowth = Decimal.max(new Decimal(0), Decimal.min(pool, totalExempt));
    const exemptShares = this.memberExemptShares(shareableMembers, takes, takesWithoutExempt, exemptGrowth);
    for (const member of shareableMembers) {
      const result = takes.get(this.memberPerformerId(member));
      let depositShare;
      if (!totalDeposits.greaterThan(0)) {
        depositShare = new Decimal(0);
      } else if (sumTakes.greaterThan(0)) {
        depositShare = totalDeposits.times(result.take).dividedBy(sumTakes);
      } else {
        // A flopped door deal can leave EVERY take at $0 while a real deposit was
        // already paid. Take-weighting would divide by zero and silently drop the
        // deposit from the tax base entirely (so the prepayment escapes withholding
        // — the inverse error of the per-member over-withholding B3 fixed). Split it
        // evenly so the one deposit is still taxed once across the pool.
        depositShare = totalDeposits.dividedBy(shareableMembers.length);
      }
      const exemptShare = exemptShares.get(this.memberPerformerId(member));
      result.settlementTake = result.take.minus(
        this.memberTaxWithholding(member, result.take, { depositShare, exemptShare })
      );
    }

    // `oversubscribed` above is decided from the PRE-floor arithmetic, but
    // buildRemainderResult can RAISE a member to their floor afterwards — and the
    // unknown-strategy safety net can strand one — so a final take can overdraw
    // the pool while the earlier check saw it balance, leaving the preview silent
    // about a lineup allocated more than the pool holds. Re-check against the
    // final takes. Display-only: this flags the condition, it does not reallocate.
    //
    // A cent of tolerance. Member takes are each 2dp-rounded while dollar_amount
    // is decimal(15,3), so sub-cent guarantees can push the sum a penny past the
    // pool — and this flag prints a banner on the artist-facing settlement
    // statement, which has a PDF. A penny is rounding, not an overdraw.
    if (sumTakes.minus(pool).greaterThan(new Decimal('0.01'))) oversubscribed = true;

    // What the pool holds that nobody was paid. There is an `oversubscribed`
    // banner for allocating too MUCH and, until now, no counterpart for too
    // little — so a 60/30 typo, or a versus member who took their guarantee and
    // forfeited their share, left money with the venue and looked like a normal
    // split. Positive means unclaimed; the previews surface it.
    const allocated = [...takes.values()].reduce(
      (sum, result) => sum.plus(new Decimal(result.take || 0)), new Decimal(0),
    );
    const unallocated = Decimal.max(new Decimal(0), pool.minus(allocated));

    if (attributeOverflow) {
      this.attributeCapOverflow(offer, members, pool, explicitMembers, takes, { mode, poolMode });
    }

    return { takes, leftover, oversubscribed, unallocated: unallocated.toNumber() };
  }

  capOverflowExcess(result) {
    return Decimal.max(new Decimal(0), result.rawTake.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).minus(result.take));
  }

  splitByWeight(total, weights) {
    const weightTotal = weights.reduce((sum, weight) => sum.plus(weight), new Decimal(0));
    let given = new Decimal(0);
    return weights.map((weight, index) => {
      const portion = index === weights.length - 1
        ? total.minus(given)
        : total.times(weight).dividedBy(weightTotal).toDecimalPlaces(2, Decimal.ROUND_DOWN);
      given = given.plus(portion);
      return portion;
    });
  }

  attributeCapOverflow(offer, members, pool, explicitMembers, takes, { mode, poolMode }) {
    for (const result of takes.values()) {
      result.capOverflowRecipients = [];
      result.capOverflowRetained = result.capped ? this.capOverflowExcess(result) : new Decimal(0);
    }

    const cappedResults = explicitMembers
      .map((member) => takes.get(this.memberPerformerId(member)))
      .filter((result) => result.capped);
    const excesses = cappedResults.map((result) => this.capOverflowExcess(result));
    const excessTotal = excesses.reduce((sum, excess) => sum.plus(excess), new Decimal(0));
    if (!excessTotal.greaterThan(0)) return;
    if (this.skipCapOverflowAttribution) return;

    const counterfactual = this.computePoolMemberAllocation(offer, members, pool, {
      mode, poolMode, attributeExemption: false, attributeOverflow: false, heldBackFromLeftover: excessTotal,
    });

    const recipients = [];
    for (const [performerId, result] of takes) {
      const counterfactualResult = counterfactual.takes.get(performerId);
      if (!counterfactualResult) continue;
      const gain = result.take.minus(counterfactualResult.take);
      if (gain.greaterThan(0)) recipients.push({ performerId, amount: gain });
    }
    const gainTotal = recipients.reduce((sum, recipient) => sum.plus(recipient.amount), new Decimal(0));
    const retainedTotal = Decimal.max(new Decimal(0), excessTotal.minus(gainTotal));

    const destinations = recipients.map((recipient) => ({ performerId: recipient.performerId, weight: recipient.amount, isVenue: false }));
    if (retainedTotal.greaterThan(0)) destinations.push({ performerId: null, weight: retainedTotal, isVenue: true });
    cappedResults.forEach((result, index) => {
      result.capOverflowRetained = new Decimal(0);
      this.splitByWeight(excesses[index], destinations.map((destination) => destination.weight))
        .forEach((amount, destinationIndex) => {
          if (!amount.greaterThan(0)) return;
          const destination = destinations[destinationIndex];
          if (destination.isVenue) {
            result.capOverflowRetained = amount;
          } else {
            result.capOverflowRecipients.push({ performerId: destination.performerId, amount });
          }
        });
    });
  }

  buildRemainderResult(member, take, takeWithoutExempt = null) {
    // The remainder / equal-share take is the pool leftover (+ any guarantee).
    // Apply the member's own cap/floor exactly as an explicit member's share is
    // clamped (O-2783 R6) so cap/floor work on every strategy, not just explicit:
    // floor raises to the member's minimum, cap clamps down and the excess is not
    // paid (it stays with the venue — capOverflowTarget null). Members with no
    // cap/floor are byte-identical to the previous behavior.
    const rawTake = Decimal.max(new Decimal(0), take.toDecimalPlaces(2, Decimal.ROUND_HALF_UP));
    const floor = member.artist_payout_floor != null ? this.parseMaskedInput(member.artist_payout_floor) : null;
    const cap = this.parseCapOrNull(member.artist_payout_cap);

    let combined = rawTake;
    let floored = false;
    let capped = false;
    if (floor !== null && combined.lessThan(floor)) { combined = floor; floored = true; }
    if (cap !== null && combined.greaterThan(cap)) { combined = cap; capped = true; }

    const capOverflow = (capped && cap !== null) ? rawTake.minus(cap) : new Decimal(0);
    const finalTake = Decimal.max(new Decimal(0), combined.toDecimalPlaces(2, Decimal.ROUND_HALF_UP));
    const exemptShare = takeWithoutExempt === null
      ? new Decimal(0)
      : Decimal.max(new Decimal(0), finalTake.minus(takeWithoutExempt));
    const settlementTake = finalTake.minus(this.memberTaxWithholding(member, finalTake, { exemptShare }));
    return {
      take: finalTake,
      rawTake,
      capped,
      floored,
      breakevenMet: true,
      capOverflow,
      settlementTake,
    };
  }

  memberPerformerId(member) {
    return member.performer_id != null ? member.performer_id : (member.performerId != null ? member.performerId : null);
  }

  calculateDealBracketAmounts(mode) {
    const sortedBrackets = [...this.dealBrackets].sort((a, b) => (a.position || 0) - (b.position || 0));
    const ticketRows = this.dealBracketTicketRows(mode);

    const revenueBaseAmount = this.getRevenueBase(mode || 'offer');
    let revenueBaseRatio = new Decimal(1);
    if (revenueBaseAmount !== null) {
      const gross = new Decimal(this.grossPotentialForMode(mode));
      if (!gross.isZero()) {
        revenueBaseRatio = revenueBaseAmount.dividedBy(gross);
      }
    }

    const perBracketGross = this.budget.deal_bracket_basis === FinancialSummaryMath.GROSS_TICKET_SALES_BASIS
      ? this.grossBracketAllocation(sortedBrackets, ticketRows)
      : this.ticketCountBracketAllocation(sortedBrackets, ticketRows);
    const perBracketRevenue = perBracketGross.map((bracketGross) => bracketGross.times(revenueBaseRatio));
    const reachedBracketNumber = perBracketGross.reduce(
      (reached, bracketGross, index) => (bracketGross.greaterThan(0) ? index + 1 : reached),
      null,
    );

    const aggregateBracketRevenue = perBracketRevenue.reduce((sum, r) => sum.plus(r), new Decimal(0));
    let breakevenScale = new Decimal(1);
    if (this.offer.breakeven_threshold != null && !aggregateBracketRevenue.isZero()) {
      const threshold = this.parseMaskedInput(this.offer.breakeven_threshold);
      const remaining = Decimal.max(new Decimal(0), aggregateBracketRevenue.minus(threshold));
      breakevenScale = remaining.dividedBy(aggregateBracketRevenue);
    }

    let totalGuarantee = new Decimal(0);
    let totalPercentage = new Decimal(0);
    const perBracket = [];

    sortedBrackets.forEach((bracket, idx) => {
      const bracketDollarAmount = this.parseMaskedInput(bracket.dollar_amount);
      const bracketUpsidePercentage = this.parseMaskedInput(bracket.upside_percentage);
      const operator = bracket.dollar_or_percentage_operator || 'AND';

      let bracketGuarantee = new Decimal(0);
      let bracketPercentage = new Decimal(0);

      if (perBracketRevenue[idx].greaterThan(0)) {
        const gatedRevenue = perBracketRevenue[idx].times(breakevenScale);
        bracketPercentage = gatedRevenue.times(bracketUpsidePercentage).dividedBy(100);

        if (operator === 'OR') {
          if (bracketDollarAmount.greaterThan(bracketPercentage)) {
            bracketGuarantee = bracketDollarAmount;
            bracketPercentage = new Decimal(0);
          } else {
            bracketGuarantee = new Decimal(0);
          }
        } else {
          bracketGuarantee = bracketDollarAmount;
        }
      }

      totalGuarantee = totalGuarantee.plus(bracketGuarantee);
      totalPercentage = totalPercentage.plus(bracketPercentage);
      perBracket.push({
        bracketId: bracket.id,
        position: bracket.position || 0,
        capOverflowTarget: bracket.cap_overflow_target || null,
        payout: bracketGuarantee.plus(bracketPercentage),
      });
    });

    const percentageInEffect = sortedBrackets.some((bracket, index) => (
      perBracketRevenue[index].greaterThan(0) && this.parseMaskedInput(bracket.upside_percentage).greaterThan(0)
    ));

    return {
      totalGuarantee, totalPercentage, perBracket, aggregateRevenue: aggregateBracketRevenue, percentageInEffect, reachedBracketNumber,
    };
  }

  dealBracketReached(mode) {
    const reachedBracketNumber = this.bracketResultsByMode?.[mode]?.reachedBracketNumber;
    if (!reachedBracketNumber) return null;
    return { number: reachedBracketNumber, bracketCount: this.dealBrackets.length };
  }

  dealBracketTicketRows(mode) {
    return (this.ticketsSection.items || [])
      .filter(item => item.calculatedLeftHand > 0)
      .filter(item => item.show_in_offer !== false)
      .map(item => {
        const quantity = mode === 'forecast'
          ? (item.calculatedRightHandForecast || 0)
          : mode === 'actual'
            ? (item.calculatedRightHandActual || 0)
            : (item.calculatedRightHand || 0);
        const price = mode === 'forecast'
          ? (item.calculatedLeftHandForecast || 0)
          : mode === 'actual'
            ? (item.calculatedLeftHandActual || 0)
            : (item.calculatedLeftHand || 0);
        return { item, price, quantity: Math.max(0, Math.round(quantity)) };
      });
  }

  ticketCountBracketAllocation(sortedBrackets, ticketRows) {
    const soldTickets = ticketRows.flatMap(row => Array(row.quantity).fill(row.price));
    return sortedBrackets.map((bracket) => {
      if (soldTickets.length === 0) return new Decimal(0);
      const allocatedTickets = soldTickets.splice(0, Math.min(bracket.quantity || 0, soldTickets.length));
      return new Decimal(allocatedTickets.reduce((sum, price) => sum + price, 0));
    });
  }

  grossTicketSales(rows) {
    return rows
      .reduce((sum, row) => sum.plus(new Decimal(row.price).times(row.quantity)), new Decimal(0))
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  }

  dealBracketGrossWidth(bracket) {
    return this.parseMaskedInput(bracket.gross_amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  }

  grossBracketAllocation(sortedBrackets, ticketRows) {
    let remaining = this.grossTicketSales(ticketRows);
    return sortedBrackets.map((bracket) => {
      const width = this.dealBracketGrossWidth(bracket);
      const allocated = Decimal.min(width, remaining);
      remaining = remaining.minus(allocated);
      return allocated;
    });
  }

  dealBracketGrossAtTicketCount(ticketCount, mode) {
    const allocation = this.allocateTicketQuantities(ticketCount, mode);
    if (allocation === null) return null;

    const allocatedByRowId = new Map(allocation.map((entry) => [entry.rowId, entry.quantity]));
    return this.grossTicketSales(this.dealBracketTicketRows(mode).map((row) => {
      const rowId = this.ticketRowIdentity(row.item);
      return allocatedByRowId.has(rowId) ? { ...row, quantity: allocatedByRowId.get(rowId) } : row;
    }));
  }

  ticketCountAtGross(grossLimit, mode) {
    let low = 0;
    let high = FinancialSummaryMath.BREAKEVEN_MAX_TICKETS;
    const grossAtHigh = this.dealBracketGrossAtTicketCount(high, mode);
    if (grossAtHigh === null || grossAtHigh.lessThanOrEqualTo(grossLimit)) return null;

    while (low + 1 < high) {
      const middle = Math.floor((low + high) / 2);
      if (this.dealBracketGrossAtTicketCount(middle, mode).lessThanOrEqualTo(grossLimit)) {
        low = middle;
      } else {
        high = middle;
      }
    }
    return low;
  }

  sectionTotalForMode(section, mode) {
    if (!section) return 0;
    if (mode === 'forecast') return section.externalForecastTotal || 0;
    if (mode === 'actual') return section.externalActualTotal || 0;
    return section.externalTotal || 0;
  }

  sectionGrossTotalForMode(section, mode) {
    if (!section) return 0;
    if (mode === 'forecast') return section.forecastTotal || 0;
    if (mode === 'actual') return section.actualTotal || 0;
    return section.total || 0;
  }

  ticketsToBreakevenDetail(mode) {
    const totalTickets = this.numberOfPaidTickets(mode);
    const totalRevenue = this.sectionTotalForMode(this.ticketsSection, mode);

    let weightedAverageTicketPrice = 0;
    if (totalTickets > 0 && totalRevenue > 0) {
      weightedAverageTicketPrice = new Decimal(totalRevenue).dividedBy(totalTickets).toNumber();
    }

    return {
      totalTickets,
      totalRevenue,
      weightedAverageTicketPrice,
    };
  }

  paidTicketRowsForMode(mode) {
    const priceKey = mode === 'forecast' ? 'calculatedLeftHandForecast'
      : mode === 'actual' ? 'calculatedLeftHandActual'
        : 'calculatedLeftHand';
    const quantityKey = mode === 'forecast' ? 'calculatedRightHandForecast'
      : mode === 'actual' ? 'calculatedRightHandActual'
        : 'calculatedRightHand';
    const calcTypeKey = mode === 'forecast' ? 'calc_type_forecast'
      : mode === 'actual' ? 'calc_type_actual'
        : 'calc_type';

    const rows = [];
    const items = (this.ticketsSection && this.ticketsSection.items) || [];
    items.forEach((item, index) => {
      if (item.show_in_offer === false) return;
      const calcType = mode === 'actual'
        ? (item.calc_type_actual || item.calc_type_forecast || item.calc_type || 'fixed')
        : (item[calcTypeKey] || item.calc_type || 'fixed');
      if (calcType !== 'fixed') return;
      if (!((item[priceKey] || 0) > 0)) return;
      const quantity = item[quantityKey] || 0;
      if (!(quantity > 0)) return;
      rows.push({ item, quantity });
    });
    return rows;
  }

  allocateTicketQuantities(targetTickets, mode) {
    const rows = this.paidTicketRowsForMode(mode);
    const plannedTickets = rows.reduce((sum, row) => sum + row.quantity, 0);
    if (!(plannedTickets > 0)) return null;

    const exactShares = rows.map((row) => (row.quantity * targetTickets) / plannedTickets);
    const allocated = exactShares.map((share) => Math.floor(share));
    let unallocated = targetTickets - allocated.reduce((sum, value) => sum + value, 0);

    const byLargestRemainder = exactShares
      .map((share, position) => ({ position, remainder: share - Math.floor(share) }))
      .sort((a, b) => (b.remainder - a.remainder) || (a.position - b.position));

    let cursor = 0;
    while (unallocated > 0 && cursor < byLargestRemainder.length) {
      allocated[byLargestRemainder[cursor].position] += 1;
      unallocated -= 1;
      cursor += 1;
    }

    const allocation = rows.map((row, position) => ({
      rowId: this.ticketRowIdentity(row.item),
      quantity: allocated[position],
    }));

    if (allocation.some((entry) => entry.rowId === null)) return null;

    return allocation;
  }

  //
  ticketRowIdentity(item) {
    if (item.uuid) return `uuid:${item.uuid}`;
    if (item.id) return `id:${item.id}`;
    if (item.overridable_type && item.overridable_id) {
      return `${item.overridable_type}:${item.overridable_id}`;
    }
    return null;
  }

  budgetWithTicketQuantities(allocation, mode) {
    const quantityKey = mode === 'forecast' ? 'right_hand_forecast'
      : mode === 'actual' ? 'right_hand_actual'
        : 'right_hand';

    const quantityByRowId = new Map(
      allocation.filter((entry) => entry.rowId != null).map((entry) => [entry.rowId, entry.quantity])
    );

    const rescaled = (this.budget[this.ticketLineItemsKey] || []).map((item) => {
      const rowId = this.ticketRowIdentity(item);
      return quantityByRowId.has(rowId)
        ? { ...item, [quantityKey]: quantityByRowId.get(rowId) }
        : item;
    });

    return { ...this.budget, [this.ticketLineItemsKey]: rescaled };
  }

  venueNetAtTicketCount(targetTickets, mode) {
    const allocation = this.allocateTicketQuantities(targetTickets, mode);
    if (allocation === null) return null;

    const probe = new this.constructor(
      this.offer,
      this.budgetWithTicketQuantities(allocation, mode),
      this.isSettling
    );
    probe.skipCapOverflowAttribution = true;
    probe.calculate();

    const netKey = mode === 'forecast' ? 'venueNetPotentialForecast'
      : mode === 'actual' ? 'venueNetPotentialActual'
        : 'venueNetPotential';
    const ancillaryKey = mode === 'forecast' ? 'ancillaryNetForecast'
      : mode === 'actual' ? 'ancillaryNetActual'
        : 'ancillaryNet';
    const total = (section) => probe.sectionTotalForMode(section, mode);
    const grossTotal = (section) => probe.sectionGrossTotalForMode(section, mode);

    return {
      tickets: probe.numberOfPaidTickets(mode),
      venueNet: probe.showSummary[netKey] || 0,
      venueNetExcludingAncillary: new Decimal(probe.showSummary[netKey] || 0)
        .minus(probe.showSummary[ancillaryKey] || 0).toNumber(),
      ticketRevenue: total(probe.ticketsSection),
      otherIncome: grossTotal(probe.revenueSection),
      beforeAdjustedDeductions: total(probe.beforeAdjustedDeductionsSection),
      venueExpenses: grossTotal(probe.expensesSection) + grossTotal(probe.finalExpensesSection),
      artistPayout: probe.artistEarningsForMode(mode).artistTotalDue,
    };
  }

  sortedDealBrackets() {
    return [...this.dealBrackets].sort((a, b) => (a.position || 0) - (b.position || 0));
  }

  bonusTicketThreshold(bonus) {
    return this.parseMaskedInput(bonus.ticket_count_requirement).toNumber();
  }

  ticketCountDiscontinuities(mode) {
    const thresholds = [];

    this.budgetBonuses.forEach((bonus) => {
      if (!bonus.ticket_count_requirement) return;
      const isPercentage = bonus.upside_percentage && bonus.dollar_or_percentage_operator === 'PERCENTAGE';
      const isCash = bonus.dollar_amount && bonus.dollar_or_percentage_operator === 'CASH';
      if (!isPercentage && !isCash) return;
      thresholds.push(this.bonusTicketThreshold(bonus));
    });

    if (!this.offer.from_dollar_one) {
      const grossBasis = this.budget.deal_bracket_basis === FinancialSummaryMath.GROSS_TICKET_SALES_BASIS;
      let cumulative = new Decimal(0);
      this.sortedDealBrackets().forEach((bracket) => {
        cumulative = cumulative.plus(grossBasis ? this.dealBracketGrossWidth(bracket) : this.parseMaskedInput(bracket.quantity));
        thresholds.push(grossBasis ? this.ticketCountAtGross(cumulative, mode) : cumulative.toNumber());
      });
    }

    return thresholds
      .filter((threshold) => threshold > 0 && Number.isFinite(threshold))
      .map((threshold) => Math.ceil(threshold))
      .sort((a, b) => a - b);
  }

  solveTicketsToBreakeven(mode) {
    const detail = this.ticketsToBreakevenDetail(mode);

    const outcome = (achievable) => ({
      breakeven: 0,
      achievable,
      beyondPlannedTickets: false,
      ticketRevenueAtBreakeven: 0,
      otherIncomeAtBreakeven: 0,
      beforeAdjustedDeductionsAtBreakeven: 0,
      venueExpensesAtBreakeven: 0,
      artistPayoutAtBreakeven: 0,
      venueNetAtBreakeven: 0,
    });
    const indeterminate = outcome(null);
    const neverBreaksEven = outcome(false);

    const plannedTickets = Math.ceil(detail.totalTickets);
    if (!(plannedTickets > 0)) return indeterminate;
    if (!(detail.totalRevenue > 0)) return indeterminate;

    const probe = (ticketCount) => {
      if (this.breakevenProbesUsed >= this.breakevenProbeBudget) return null;
      this.breakevenProbesUsed += 1;
      return this.venueNetAtTicketCount(ticketCount, mode);
    };

    const solvedFrom = (result) => ({
      breakeven: result.tickets,
      achievable: true,
      beyondPlannedTickets: result.tickets > plannedTickets,
      ticketRevenueAtBreakeven: result.ticketRevenue,
      otherIncomeAtBreakeven: result.otherIncome,
      beforeAdjustedDeductionsAtBreakeven: result.beforeAdjustedDeductions,
      venueExpensesAtBreakeven: result.venueExpenses,
      artistPayoutAtBreakeven: result.artistPayout,
      venueNetAtBreakeven: result.venueNet,
    });

    const atZero = probe(0);
    if (atZero === null) return indeterminate;
    if (atZero.venueNetExcludingAncillary >= 0) return solvedFrom(atZero);

    const searchCeiling = Math.min(
      Math.max(
        plannedTickets * FinancialSummaryMath.BREAKEVEN_MAX_PLAN_MULTIPLE,
        FinancialSummaryMath.BREAKEVEN_MIN_SEARCH_CEILING
      ),
      FinancialSummaryMath.BREAKEVEN_MAX_TICKETS
    );
    const clampedByTicketCap = plannedTickets > searchCeiling;
    let ceiling = Math.min(plannedTickets, searchCeiling);
    let atCeiling = probe(ceiling);
    if (atCeiling === null) return indeterminate;
    while (atCeiling.venueNetExcludingAncillary < 0 && ceiling < searchCeiling) {
      const nextCeiling = Math.min(ceiling * 2, searchCeiling);
      const atNextCeiling = probe(nextCeiling);
      if (atNextCeiling === null) return indeterminate;
      ceiling = nextCeiling;
      atCeiling = atNextCeiling;
    }

    const segmentStarts = [0].concat(
      this.ticketCountDiscontinuities(mode).filter((threshold) => threshold > 0 && threshold <= ceiling)
    ).filter((value, index, all) => all.indexOf(value) === index);

    for (let segment = 0; segment < segmentStarts.length; segment += 1) {
      const low = segmentStarts[segment];
      const high = segment + 1 < segmentStarts.length
        ? segmentStarts[segment + 1] - 1
        : ceiling;
      const atLow = low === 0 ? atZero : probe(low);
      if (atLow === null) return indeterminate;
      if (atLow.venueNetExcludingAncillary >= 0) return solvedFrom(atLow);

      let lowBound = low;
      let highBound = high;

      let best = null;
      const budgetLeft = this.breakevenProbeBudget - this.breakevenProbesUsed;
      const samples = Math.min(FinancialSummaryMath.BREAKEVEN_SWEEP_SAMPLES,
        highBound - lowBound - 1, Math.max(0, budgetLeft - 2));

      const sweepOrigin = lowBound;
      const sweepSpan = highBound - lowBound;

      for (let sample = 1; sample <= samples && best === null; sample += 1) {
        const count = Math.round(sweepOrigin + ((sweepSpan * sample) / (samples + 1)));
        if (count <= lowBound || count >= highBound) continue;
        const atSample = probe(count);
        if (atSample === null) return indeterminate;
        if (atSample.venueNetExcludingAncillary >= 0) {
          best = atSample;
          highBound = count;
        } else {
          lowBound = count;
        }
      }

      if (best === null) {
        best = highBound === ceiling ? atCeiling : probe(highBound);
        if (best === null) return indeterminate;
        if (best.venueNetExcludingAncillary < 0) continue;
      }

      while (lowBound + 1 < highBound) {
        const middle = Math.floor((lowBound + highBound) / 2);
        const atMiddle = probe(middle);
        if (atMiddle === null) return indeterminate;
        if (atMiddle.venueNetExcludingAncillary >= 0) {
          highBound = middle;
          best = atMiddle;
        } else {
          lowBound = middle;
        }
      }

      return solvedFrom(best);
    }

    if (clampedByTicketCap) return indeterminate;
    if (atCeiling.venueNetExcludingAncillary > atZero.venueNetExcludingAncillary) return indeterminate;
    return neverBreaksEven;
  }

  attachTicketsToBreakeven() {
    const modes = ['offer', 'forecast'];
    this.breakevenProbeBudget = Math.floor(FinancialSummaryMath.BREAKEVEN_MAX_PROBES / modes.length);

    modes.forEach((mode) => {
      const earnings = this.artistEarningsForMode(mode);
      if (!earnings) return;
      this.breakevenProbesUsed = 0;
      const solved = this.solveTicketsToBreakeven(mode);
      earnings.ticketsToBreakeven = solved.breakeven;
      earnings.ticketsToBreakevenDetail = { ...earnings.ticketsToBreakevenDetail, ...solved };
    });
  }

  numberOfPaidTickets(mode) {
    if (!this.ticketsSection || !this.ticketsSection.items) return 0;
    let count = 0;
    for (const item of this.ticketsSection.items) {
      if (item.show_in_offer === false) continue;

      if (mode === 'forecast') {
        if (item.calculatedLeftHandForecast > 0) count += item.calculatedRightHandForecast;
      } else if (mode === 'actual') {
        if (item.calculatedLeftHandActual > 0) count += (item.calculatedRightHandActual || 0);
      } else {
        if (item.calculatedLeftHand > 0) count += item.calculatedRightHand;
      }
    }
    return count;
  }

  buildAdditionLineItems() {
    const items = [...this.additionLineItems];
    const paidTicketsCount = this.numberOfPaidTickets();

    const cashBonuses = this.budgetBonuses
      .filter(b => b.ticket_count_requirement)
      .filter(b => b.dollar_amount)
      .filter(b => b.dollar_or_percentage_operator === 'CASH')
      .filter(b => {
        const req = typeof b.ticket_count_requirement === 'string'
          ? parseFloat(String(b.ticket_count_requirement).replace(/,/g, ''))
          : b.ticket_count_requirement;
        return req <= paidTicketsCount;
      })
      .sort((a, b) => a.ticket_count_requirement - b.ticket_count_requirement)
      .map(b => {
        const dollarAmount = this.parseMaskedInput(b.dollar_amount).toNumber();

        return {
          name: `Bonus for ${b.ticket_count_requirement} tickets`,
          left_hand: dollarAmount,
          left_hand_forecast: null,
          left_hand_actual: null,
          operator: '*',
          right_hand: 1,
          right_hand_forecast: null,
          right_hand_actual: null,
          show_in_offer: true,
          calc_type: 'fixed',
        };
      });

    return items.concat(cashBonuses);
  }

  finalUpsidePercentage(paidTicketsCount) {
    const qualifyingBonus = this.budgetBonuses
      .filter(b => b.upside_percentage)
      .filter(b => b.ticket_count_requirement)
      .filter(b => b.dollar_or_percentage_operator === 'PERCENTAGE')
      .filter(b => {
        const req = typeof b.ticket_count_requirement === 'string'
          ? parseFloat(String(b.ticket_count_requirement).replace(/,/g, ''))
          : b.ticket_count_requirement;
        return req <= paidTicketsCount;
      })
      .sort((a, b) => a.ticket_count_requirement - b.ticket_count_requirement)
      .pop();

    if (qualifyingBonus) {
      return this.parseMaskedInput(qualifyingBonus.upside_percentage);
    }
    return this.parseMaskedInput(this.offer.upside_percentage);
  }

  grossPotential() {
    return new Decimal(this.ticketsSection.externalTotal || 0)
      .plus(new Decimal(this.revenueSection.externalTotal || 0))
      .toNumber();
  }

  grossPotentialForMode(mode) {
    if (mode === 'forecast') {
      return new Decimal(this.ticketsSection.externalForecastTotal || 0)
        .plus(new Decimal(this.revenueSection.externalForecastTotal || 0))
        .toNumber();
    } else if (mode === 'actual') {
      return new Decimal(this.ticketsSection.externalActualTotal || 0)
        .plus(new Decimal(this.revenueSection.externalActualTotal || 0))
        .toNumber();
    }
    return this.grossPotential();
  }

  facilityMaintenanceFeeTotal(mode) {
    const key = mode === 'forecast'
      ? 'calculatedForecastTotal'
      : mode === 'actual'
        ? 'calculatedActualTotal'
        : 'calculatedTotal';
    const items = (this.beforeAdjustedDeductionsSection && this.beforeAdjustedDeductionsSection.items) || [];
    return items.reduce((sum, item) => {
      const isFacilityFee = item.kind === 'facility_maintenance_fee'
        || item.name === 'Facilities Maintenance Fee';
      if (!isFacilityFee) return sum;
      if (item.show_in_offer === false) return sum;
      return sum.plus(new Decimal(item[key] || 0));
    }, new Decimal(0));
  }

  // A before-adjusted-deduction row that represents admission/sales tax. The
  // facility fee and charity are explicitly NOT tax and stay as real
  // deductions. A row counts as tax when it is a settlement-injected per-ticket
  // ManualTax row, is explicitly marked kind 'admission_tax', or is a
  // percentage-of-the-gate tax line (facility fee / charity use per-ticket or
  // fixed calc types, never these) — so a venue's existing "% of gross" tax
  // line is picked up automatically when the toggle is turned on. (O-4509)
  // A before-adjusted row that IS the admission tax. Only ever true for a row
  // the venue explicitly marked (`kind`), the ManualTax forecast synced onto
  // the offer, or a real per-ticket ManualTax row injected at settlement.
  //
  // Deliberately no inference from calc_type: guessing "any % of gross row is
  // tax" would sweep in a percentage-based promoter fee and quietly hand the
  // artist a bigger split. If nothing is marked, the toggle simply does
  // nothing — a visible no-op beats a wrong payout. (O-4509)
  isAdmissionTaxRow(item) {
    if (!item) return false;
    if (item.kind === 'facility_maintenance_fee' || item.kind === 'charity_deduction') return false;
    return item.kind === 'admission_tax'
      || item.kind === 'manual_tax_projection'
      || item.overridable_type === 'ManualTax';
  }

  selectAdmissionTaxRows(rows) {
    return rows.filter((i) => this.isAdmissionTaxRow(i));
  }

  // True when this tax is already counted inside the gate, i.e. settlement
  // grouped the ticket price with the tax included (`hide_tax_in_offer` off).
  // Such a row must stay a deduction; see the partition in calculate().
  isTaxInsideGate(item) {
    return !!item && item.included_in_gross === true;
  }

  // A lifted tax row is charged on the net gate, so its own figures have to say
  // the same thing the pass-through line does. Left alone the row reports the
  // tax on GROSS while the summary reports it on the net gate — two numbers for
  // one tax, in a section whose total excludes it. Restates each lifted row on
  // the net gate, splitting the pass-through amount across them in proportion
  // to their raw share. Rows still deducting (tax inside the gate) keep their
  // real deducted figures. (O-4509)
  rebaseLiftedTaxRowsForDisplay() {
    const items = (this.admissionTaxSection && this.admissionTaxSection.items) || [];
    const lifted = items.filter((i) => !this.isTaxInsideGate(i));
    if (lifted.length === 0) return;

    const modes = [
      ['calculatedTotal', 'calculatedRightHand', this.showSummary.admissionTax, this.showSummary.netBoxOfficeReceipts],
      ['calculatedForecastTotal', 'calculatedRightHandForecast', this.showSummary.admissionTaxForecast, this.showSummary.netBoxOfficeReceiptsForecast],
      ['calculatedActualTotal', 'calculatedRightHandActual', this.showSummary.admissionTaxActual, this.showSummary.netBoxOfficeReceiptsActual],
    ];

    for (const [totalKey, rightKey, target, netGate] of modes) {
      const rawTotal = lifted.reduce((sum, i) => sum + (i[totalKey] || 0), 0);
      lifted.forEach((item) => {
        const share = rawTotal > 0
          ? new Decimal(item[totalKey] || 0).dividedBy(rawTotal)
          : new Decimal(1).dividedBy(lifted.length);
        item[totalKey] = new Decimal(target || 0).times(share)
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
        if (FinancialSummaryMath.PERCENT_CALC_TYPES.includes(item.calc_type)) item[rightKey] = netGate || 0;
      });
    }

    // Keep the section's own totals in step with the rows it now reports.
    const sum = (key) => items.reduce((total, i) => total + (i[key] || 0), 0);
    this.admissionTaxSection.total = sum('calculatedTotal');
    this.admissionTaxSection.externalTotal = sum('calculatedTotal');
    this.admissionTaxSection.forecastTotal = sum('calculatedForecastTotal');
    this.admissionTaxSection.actualTotal = sum('calculatedActualTotal');
  }

  // Tax charged on each ticket, present only on actuals fed from real sales.
  // For teams whose settlement price includes tax, that money is already inside
  // the actual gross, so it is what passes through — a rate applied to that
  // gross would tax the tax. (O-4509)
  ticketTaxTotal(mode) {
    if (mode !== 'actual') return new Decimal(0);
    const rows = this.budget.ticket_line_items || [];
    return rows.reduce((sum, row) => {
      const taxes = parseFloat(row.taxes);
      if (!taxes || taxes <= 0) return sum;
      const qty = parseFloat(row.right_hand_actual != null && row.right_hand_actual !== ''
        ? row.right_hand_actual : row.right_hand) || 0;
      return sum.plus(new Decimal(taxes).times(qty));
    }, new Decimal(0));
  }

  // Combined rate across the tax rows, when every one of them knows its rate.
  // Returns null if any row doesn't (a hand-entered flat tax, say), so the
  // caller falls back to scaling the amount.
  admissionTaxRate(rows) {
    rows = rows || this.admissionTaxRows || [];
    if (rows.length === 0) return null;
    let total = new Decimal(0);
    for (const row of rows) {
      const rate = parseFloat(row.tax_rate);
      if (!rate || rate <= 0) return null;
      total = total.plus(rate);
    }
    return total;
  }

  // The gate with tax already removed. When the tax is inside the gate it is
  // still deducting, so the net gate is (gross - tax - facility fee); when it
  // is outside, the gross never held it and only the facility fee comes off.
  // Either way this is the base the deal and the re-based tax are figured on.
  taxInsideGateTotal(mode) {
    const totalKey = mode === 'forecast' ? 'externalForecastTotal'
      : mode === 'actual' ? 'externalActualTotal'
        : 'externalTotal';
    const rows = (this.admissionTaxRows || []).filter((i) => this.isTaxInsideGate(i));
    if (rows.length === 0) return new Decimal(0);
    return new Decimal(this.calculateSection(rows)[totalKey] || 0);
  }

  // Pass-through admission tax on the net gate. Takes the offer's existing tax
  // (computed on the gross) and re-bases it to the net gate by scaling it by
  // (net gate / gross) = (gross - facility fee) / gross. This is
  // representation-agnostic — it scales the tax AMOUNT, so it works for both a
  // per-ticket ManualTax and a "% of gross" tax line. Returns 0 when off. (O-4509)
  admissionTax(mode) {
    if (!this.offer.apply_tax_after_facility_fee) return 0;

    // externalTotal (show_in_offer-filtered) is deliberate, and must stay in step
    // with SettlementMath, which expresses the same thing as `total` — that
    // engine has no externalTotal, and builds `total` from shouldShow-filtered
    // rows (SettlementMath.js:530-546). Reading the unfiltered `total` here
    // makes the two engines disagree on the same number.
    //
    // A hidden marked tax row therefore still reports 0. That is tracked on
    // O-4786 and is NOT resolved here: changing it is a money change that has to
    // land in both engines at once, deliberately reviewed.
    const totalKey = mode === 'forecast' ? 'forecastTotal'
      : mode === 'actual' ? 'actualTotal'
        : 'externalTotal';

    // Tax inside the gate passes through at exactly what was collected —
    // re-basing tax the buyer already paid would report a figure nobody was
    // charged. Only tax outside the gate is charged on the net gate per the
    // deal terms, so only that part is ever re-based. (O-4509)
    // Real per-ticket tax supersedes the offer's forecast for that mode, the
    // same way settlement replaces the forecast with the rows actually charged.
    const collected = this.ticketTaxTotal(mode);
    if (collected.greaterThan(0)) {
      return collected.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
    }

    const insideTotal = this.taxInsideGateTotal(mode);
    const liftedRows = ((this.admissionTaxSection && this.admissionTaxSection.items) || [])
      .filter((i) => !this.isTaxInsideGate(i));

    let lifted = new Decimal(0);
    if (liftedRows.length > 0) {
      const liftedRaw = new Decimal(this.calculateSection(liftedRows)[totalKey] || 0);
      if (liftedRaw.greaterThan(0)) {
        const gross = new Decimal(this.grossPotentialForMode(mode)).minus(insideTotal);
        if (gross.greaterThan(0)) {
          // A facility fee larger than the gate would otherwise produce a
          // negative pass-through; there is no such thing as negative tax.
          const netGate = Decimal.max(gross.minus(this.facilityMaintenanceFeeTotal(mode)), 0);
          // The deal is written against the rate, so prefer it over the
          // collected amount, whose per-ticket rounding drifts.
          const rate = this.admissionTaxRate(liftedRows);
          lifted = rate
            ? netGate.times(rate).dividedBy(100)
            : liftedRaw.times(netGate).dividedBy(gross);
        }
      }
    }

    return insideTotal.plus(lifted).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
  }

  getDeductedBeforeSplitTotal(mode) {
    const items = (this.expensesSection?.items || [])
      .concat(this.finalExpensesSection?.items || []);

    let total = new Decimal(0);
    for (const item of items) {
      if (item.deducted_before_split && item.show_in_offer !== false) {
        const value = mode === 'forecast'
          ? (item.calculatedForecastTotal || 0)
          : mode === 'actual'
            ? (item.calculatedActualTotal || 0)
            : (item.calculatedTotal || 0);
        total = total.plus(value);
      }
    }
    return total;
  }

  getRevenueBase(mode) {
    const revenueBase = this.offer.revenue_base || 'default';

    if (revenueBase === 'default') return null;

    if (revenueBase === 'gross') {
      return new Decimal(this.grossPotentialForMode(mode));
    }

    const netBORKey = mode === 'forecast' ? 'netBoxOfficeReceiptsForecast'
      : mode === 'actual' ? 'netBoxOfficeReceiptsActual'
      : 'netBoxOfficeReceipts';
    let base = new Decimal(this.showSummary[netBORKey] || 0);

    if (revenueBase === 'adjusted_gross') {
      base = base.minus(this.getDeductedBeforeSplitTotal(mode));
    }

    return base;
  }

  buildExpenseItems() {
    return this.expenseLineItems.filter(item => !FinancialSummaryMath.isFinalExpenseItem(item));
  }

  buildFinalExpenseItems() {
    return this.expenseLineItems.filter(item => FinancialSummaryMath.isFinalExpenseItem(item));
  }

  calculateShowNetAfterFinalExpenses() {
    const showNetAfterFinalExpenses = new Decimal(this.showSummary.showNetAfterExpenses || 0)
      .minus(this.finalExpensesSection.externalTotal || 0);

    const showNetAfterFinalExpensesForecast = new Decimal(this.showSummary.showNetAfterExpensesForecast || 0)
      .minus(this.finalExpensesSection.externalForecastTotal || 0);

    const showNetAfterFinalExpensesActual = new Decimal(this.showSummary.showNetAfterExpensesActual || 0)
      .minus(this.finalExpensesSection.externalActualTotal || 0);

    return {
      ...this.showSummary,
      showNetAfterFinalExpenses: showNetAfterFinalExpenses.toNumber(),
      showNetAfterFinalExpensesForecast: showNetAfterFinalExpensesForecast.toNumber(),
      showNetAfterFinalExpensesActual: showNetAfterFinalExpensesActual.toNumber(),
    };
  }

  // O-4755. A payout cap of 0 means "no cap", exactly like a blank one. 0 is how
  // a booker clears the field, so reading it literally would turn a routine edit
  // into a zeroed settlement — every take clamped to nothing. A floor needs no
  // equivalent: 0 is already inert there, since a take is never below zero.
  parseCapOrNull(value) {
    if (value == null || value === '') return null;
    const parsed = this.parseMaskedInput(value);
    return parsed.greaterThan(0) ? parsed : null;
  }

  parseMaskedInput(value) {
    if (value == null || value === '') return new Decimal(0);
    if (typeof value === 'number') return new Decimal(value);
    const cleaned = String(value).replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? new Decimal(0) : new Decimal(parsed);
  }
}

if (typeof module !== 'undefined') {
  module.exports = { FinancialSummaryMath };
}
